import { loadBackgroundRuntime } from "./background-runtime-loader";
import {
  AgentWidgetClient,
  HistoryClientError,
  type SSEEventCallback,
} from "./client";
import { isWebMcpToolName } from "./webmcp-bridge";
import { isBrowserLocalApproval, restoreBrowserToolState } from "./utils/restored-browser-state";
import type { SessionActionsHost, SessionActionsInternals } from "./session-actions";
import {
  getSessionActionsSync,
  loadSessionActions,
  type SessionActionsModule,
} from "./session-actions-loader";
import {
  SUGGEST_REPLIES_TOOL_NAME,
  resolveFollowUpsFeature,
} from "./suggest-replies-tool";
import {
  AgentWidgetRunStartedEvent,
  AgentWidgetRunFinishedEvent,
  AgentWidgetConfig,
  AgentWidgetEvent,
  AgentWidgetMessage,
  AgentWidgetApproval,
  AgentWidgetToolCall,
  AgentWidgetApprovalDecisionOptions,
  WebMcpConfirmInfo,
  AgentExecutionState,
  ResumableHandle,
  ConversationExecutionState,
  AgentWidgetStreamCheckpoint,
  ClientSession,
  ContentPart,
  InjectMessageOptions,
  InjectAssistantMessageOptions,
  InjectUserMessageOptions,
  InjectSystemMessageOptions,
  InjectComponentDirectiveOptions,
  PersonaArtifactRecord,
  PersonaArtifactManualUpsert,
  HistoryDisplayProjection,
  HistoryScope,
  PendingDisplayProjections
} from "./types";
import {
  generateUserMessageId,
  generateAssistantMessageId
} from "./utils/message-id";
import { IMAGE_ONLY_MESSAGE_FALLBACK_TEXT, createTextPart, hasImages } from "./utils/content";
import { voicePriorToolRows } from "./utils/voice-prior-tool-results";
import {
  buildArtifactRefRawContent,
  resolveArtifactDisplayMode
} from "./utils/artifact-display";
import type {
  VoiceProvider,
  VoicePriorToolResult,
  VoiceStatus,
  VoiceConfig,
  ReadAloudState,
  SpeechEngine,
  AgentWidgetContentSegment,
  AgentWidgetContextMentionRef,
  WidgetHistoryInternals,
  ComposerOptionsPayload,
  ComposerQuote,
  ComposerResubmitOptions
} from "./types";
import { applyQuoteToContent } from "./utils/composer-quote";
import { canResubmitUserMessage, findUserTurnBoundary } from "./utils/resubmit-turn";
import { extractComponentDirectiveFromMessage } from "./utils/component-middleware";
import type { MentionSubmitBundle } from "./utils/context-mention-manager";
import type { VisitorStore, VisitorStoreChange } from "./utils/visitor-store";
import { HistoryProviderError } from "./internal/history-provider";
import type {
  HistoryConversationPatch,
  HistoryConversationSummary,
  HistoryOperationContext,
  HistoryProvider,
  PreparedHistoryActivation,
} from "./internal/history-provider";
import {
  divergentDisplayProjection,
  mergeWireMessagesById,
} from "./utils/history-messages";
// Read-aloud stays eager (small, and speech synthesis is gesture-adjacent);
// the provider runtime (factory + Runtype/browser providers + audio playback)
// ships in the lazy voice-runtime chunk, loaded in setupVoice. Support
// detection uses the eager construction-free probe.
import { BrowserSpeechEngine, pickBestVoice } from "./voice/browser-speech-engine";
import {
  ReadAloudController,
  type ReadAloudListener,
} from "./voice/read-aloud-controller";
import { isVoiceSupportedProbe, usesSessionVoice, usesSessionVoiceForConfig, voiceConnectionChanged } from "./utils/voice-support";
import { VoiceTurnTracker } from "./voice/voice-turn-tracker";
import type { KeyedVoiceTranscript } from "./voice/keyed-voice-transcript";
import type { VoiceDelegationCapture } from "./voice/voice-delegation";
import { loadVoiceRuntime, type VoiceRuntimeModule } from "./voice-runtime-loader";
import type { VoiceWiringSession } from "./voice/session-voice-wiring";
import { resolveSpeakableText } from "./utils/speech-text";
import { loadRuntypeTts } from "./voice/runtype-tts-loader";
// Type-only (erased at build): the runtime reconnect machinery ships in the
// lazy session-reconnect chunk, reached via `session-reconnect-loader.ts` in
// `beginReconnect`, so it never lands in bundles that don't opt into durable
// reconnect.
import type {
  ReconnectController,
  ReconnectHost,
} from "./session-reconnect";
import { loadSessionReconnect } from "./session-reconnect-loader";
import type { BackgroundConversationController, BackgroundCheckpoint } from "./session-background";

export type AgentWidgetSessionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "error"
  // Durable-session reconnect states. `paused`: a durable stream dropped and a
  // reconnect is pending. `resuming`: a reconnect attempt is in flight. While
  // in either, the in-progress assistant bubble stays open (not finalized) and
  // `streaming` stays true so the typing indicator persists.
  | "paused"
  | "resuming";

/**
 * Config fields the `AgentWidgetClient` reads to shape the connection and each
 * request. When NONE of these change, `updateConfig` can refresh the live
 * client in place (preserving the WebMCP bridge and any in-flight stream/resume)
 * instead of swapping in a fresh client and tearing down WebMCP state. Fields
 * outside this list (theme, copy, layout, suggestions, suggestionChips,
 * iterationDisplay, postprocessMessage, feature display toggles, …) are
 * display-only and safe to apply mid-turn: which is what a self-styling widget
 * needs when a `webmcp:*` theme tool re-renders the widget while the agent's
 * turn is still streaming.
 *
 * Compared by identity (`!==`): primitives by value, functions/objects by
 * reference. A consumer that rebuilds these objects on every render simply
 * takes the (still-correct) full-rebuild path. The default is therefore safe:
 * anything not explicitly listed here can never strand a paused turn.
 */
const CONNECTION_CONFIG_KEYS = [
  "apiUrl",
  "clientToken",
  "flowId",
  "agentId",
  "target",
  "targetProviders",
  "agent",
  "agentOptions",
  "headers",
  "getHeaders",
  "webmcp",
  "streamParser",
  "parserType",
  "contextProviders",
  "requestMiddleware",
  "modelMessageTransform",
  "customFetch",
  "backgroundConversations",
  "parseSSEEvent",
  "onSessionInit",
  "onSessionExpired",
  "getStoredSessionId",
  "setStoredSessionId",
  "getIdentityProof",
  "identityProvider",
  "getStoredConversationId",
  "setStoredConversationId",
  "clearStoredConversationId",
  "clearStoredSessionId",
] as const satisfies ReadonlyArray<keyof AgentWidgetConfig>;

function connectionConfigChanged(
  prev: AgentWidgetConfig,
  next: AgentWidgetConfig,
): boolean {
  return CONNECTION_CONFIG_KEYS.some((key) => prev[key] !== next[key]);
}

export type SessionCallbacks = {
  onConversationExecutionState?: (state: ConversationExecutionState) => void;
  onRunStarted?: (event: AgentWidgetRunStartedEvent) => void;
  onRunFinished?: (event: AgentWidgetRunFinishedEvent) => void;
  onMessagesChanged: (messages: AgentWidgetMessage[]) => void;
  onStatusChanged: (status: AgentWidgetSessionStatus) => void;
  onStreamingChanged: (streaming: boolean) => void;
  onError?: (error: Error) => void;
  onVoiceStatusChanged?: (status: VoiceStatus) => void;
  onArtifactsState?: (state: {
    artifacts: PersonaArtifactRecord[];
    selectedId: string | null;
  }) => void;
  /**
   * Durable-session reconnect lifecycle, surfaced so the UI can map it to the
   * public `stream:paused` / `stream:resuming` / `stream:resumed` controller
   * events. `paused` fires once on the drop, `resuming` once per attempt,
   * `resumed` when a reconnect reaches its terminal.
   */
  onReconnect?: (event: {
    phase: "paused" | "resuming" | "resumed";
    handle: ResumableHandle;
    attempt?: number;
  }) => void;
  /** Active-record/sendability changes the history UI renders. */
  onHistoryStateChanged?: (state: SessionHistoryState) => void;
  /** Privacy invalidation, including empty transcripts without a visible notice. */
  onHistoryScopeInvalidated?: () => void;
  /** Non-error, secret-free status notices (recovery, continuity, diagnostics). */
  onHistoryNotice?: (notice: SessionHistoryNotice) => void;
};

/** Session-owned history state; carries no credential or identity value. */
export type SessionHistoryState = {
  activeConversationId: string | null;
  conversationRevision: string | null;
  /** Cursor for "show earlier messages"; `null` at the start of the transcript. */
  nextMessageCursor: string | null;
  /** Sending is refused until the recoverable state clears. */
  recovery: "new_conversation_required" | null;
  sendBlocked: boolean;
};

/** One prepended history page, plus the cursor for the next older one. */
export type HistoryOlderPage = {
  messages: AgentWidgetMessage[];
  nextMessageCursor: string | null;
};

export type SessionHistoryNoticeCode =
  /** A 410 was recovered into a fresh conversation carrying only the new turn. */
  | "conversation_deleted_recovered"
  /** The replacement conversation could not be prepared; Retry is required. */
  | "new_conversation_required"
  /** A boot/continuity transition invalidated the local transcript. */
  | "history_continuity_reset"
  /** Projection finalization failed after its one retry; the marker is kept. */
  | "projection_finalization_failed";

export type SessionHistoryNotice = {
  code: SessionHistoryNoticeCode;
  message: string;
};

/** Session-layer history failures. Provider I/O failures use `HistoryProviderError`. */
export type SessionHistoryErrorCode =
  /** A turn is streaming or resuming; switching would abandon a live answer. */
  | "conversation_busy"
  /** The backend has not explicitly promised durable read-only replay. */
  | "background_unavailable"
  /** No history provider is installed for this widget. */
  | "history_unavailable"
  /** A later selection won; this result was discarded without side effects. */
  | "superseded"
  /** The active record is gone and a replacement could not be prepared. */
  | "new_conversation_required";

export class SessionHistoryError extends Error {
  public readonly code: SessionHistoryErrorCode;
  constructor(code: SessionHistoryErrorCode, message: string) {
    super(message);
    this.name = "SessionHistoryError";
    this.code = code;
  }
}

/**
 * Dispatch can fail in transport, stream parsing, lazy modules or application
 * callbacks. Only recognized fetch failures should suggest connectivity;
 * unknown/internal failures must not send users to debug a healthy backend.
 *
 * Callers can override the copy via `config.errorMessage` (a static string or
 * a function of the error). An override that returns an empty string yields ""
 * here, which the caller treats as "suppress the fallback bubble".
 */
function buildDispatchErrorContent(
  error: unknown,
  override?: AgentWidgetConfig["errorMessage"]
): string {
  const err = error instanceof Error ? error : new Error(String(error));

  if (typeof override === "string") return override;
  if (typeof override === "function") return override(err);

  // Fetch rejects with TypeError too, so the error class cannot distinguish
  // an offline browser from an internal fault such as a missing function.
  const detail = err.message.trim();
  const networkFailure = /^(Failed to fetch|fetch failed|Load failed|NetworkError when attempting to fetch resource\.?)$/i.test(detail);
  const backendFailure = /^(Chat backend|Agent execution) request failed: \d{3}\b/.test(detail);
  const base = networkFailure
    ? "Sorry: I couldn't reach the assistant. Please check your connection and try again."
    : backendFailure
      ? "Sorry: the chat service couldn't complete the request. Please try again."
      : "Sorry: the chat encountered an unexpected error. Please reload the page and try again.";
  return detail ? `${base}\n\n_Details: ${detail}_` : base;
}

/**
 * Tool names whose `await` the widget resolves automatically (no user
 * pill click): `webmcp:*` page tools and the built-in fire-and-forget
 * `suggest_replies`. These share the await-batch / dedupe / resume machinery;
 * `ask_user_question` is NOT one of them: it blocks on the answer sheet.
 */
const isAutoResolvedLocalToolName = (name: string): boolean =>
  isWebMcpToolName(name) || name === SUGGEST_REPLIES_TOOL_NAME;

export type SessionRunActivity = {
  run: AgentWidgetRunStartedEvent & { outcome?: AgentWidgetRunFinishedEvent["outcome"] };
  messageIds: Set<string>;
};

export class AgentWidgetSession {
  private client: AgentWidgetClient;
  private messages: AgentWidgetMessage[];
  private status: AgentWidgetSessionStatus = "idle";
  #activeRun: AgentWidgetRunStartedEvent | null = null;
  #runActivity?: SessionRunActivity;
  #runFailed = false;
  #runFinishTimer: ReturnType<typeof setTimeout> | null = null;

  #beginRun(executionId?: string): void {
    this.#finishRun("cancel");
    this.#runFailed = false;
    this.#activeRun = { runId: `run-${generateAssistantMessageId()}`, timestamp: Date.now() };
    this.#runActivity = { run: this.#activeRun, messageIds: new Set() };
    // Only a known resumed execution may adopt existing assistant rows.
    if (executionId) for (const message of this.messages) {
      if (message.role === "assistant" && message.agentMetadata?.executionId === executionId) this.#runActivity.messageIds.add(message.id);
    }
    this.callbacks.onRunStarted?.({ ...this.#activeRun });
  }

  #finishRun(
    outcome: AgentWidgetRunFinishedEvent["outcome"],
    expectedRun = this.#activeRun
  ): void {
    if (expectedRun !== this.#activeRun) return;
    if (this.#runFinishTimer !== null) clearTimeout(this.#runFinishTimer);
    this.#runFinishTimer = null;
    const run = this.#activeRun;
    this.#activeRun = null;
    if (run) {
      const finished = run as AgentWidgetRunFinishedEvent;
      finished.timestamp = Date.now();
      finished.outcome = outcome;
      this.callbacks.onRunFinished?.(finished);
    }
  }

  #scheduleRunFinish(): void {
    if (!this.#activeRun || this.#runFinishTimer !== null) return;
    const run = this.#activeRun;
    // Let dispatch rejection and batched tool resumes settle before classifying
    // an idle stream. An assistant message finishing is not a run terminal.
    this.#runFinishTimer = setTimeout(() => {
      this.#runFinishTimer = null;
      if (this.#activeRun !== run) return;
      if (!this.#runFailed && (this.streaming || this.#reconnecting ||
          this.#isAwaitPending() || this.webMcpAwaitBatches.size ||
          this.webMcpResolveControllers.size)) return;
      this.#finishRun(this.#runFailed ? "error" : "success");
    }, 0);
  }
  private streaming = false;
  /** Approval messages decided on a stream, keyed to that stream so it settles only its own. */
  #approvalSettlesAtStreamEnd = new Map<string, object | undefined>();
  /** The `connectStream` call whose event is being handled, if any. */
  #activeStreamToken: object | undefined;
  private abortController: AbortController | null = null;
  #sequenceCounter = Date.now();
  
  // Client token session management
  private clientSession: ClientSession | null = null;

  // Agent execution state
  private agentExecution: AgentExecutionState | null = null;

  // ── Durable-session reconnect ──────────────────────────────────
  // The in-flight resume handle for the durable lane (any resumable agent
  // execution the backend persists and can replay, e.g. Claude Managed agents
  // or async/background runs). Created when a stream that carries SSE `id:`
  // lines first yields a cursor (and an executionId is known); advanced as the
  // cursor climbs; cleared on a graceful terminal, a terminal error, or any
  // teardown. Drives both in-session reconnect and host persistence
  // (`onExecutionState`).
  #resumable: ResumableHandle | null = null;
  // The assistant message id of the current turn, so a reconnect keeps filling
  // the SAME bubble instead of opening a new one.
  #activeAssistantMessageId: string | null = null;
  // True while a reconnect run is active (guards against re-entry from a second
  // drop and tells the dispatch/connectStream catches to stay quiet).
  #reconnecting = false;
  #streamCheckpoint: AgentWidgetStreamCheckpoint | undefined;
  #backgroundCommitEvents: Array<{ event: AgentWidgetEvent; readOnlyReplay: boolean }> | null = null;
  #backgroundController: BackgroundConversationController | null = null;
  #backgroundIdentityUnsubscribe: (() => void) | null = null;
  // The reconnect orchestration (backoff loop, wake listeners, give-up
  // finalizer) lives in a lazily-imported module so it stays out of bundles
  // that never opt into durable reconnect. Created on the first reconnect.
  #reconnectController: ReconnectController | null = null;
  #reconnectControllerPromise: Promise<ReconnectController> | null = null;
  // Trailing-edge throttle for `onExecutionState` so it isn't fired per delta.
  #executionStateTimer: ReturnType<typeof setTimeout> | null = null;

  private artifacts = new Map<string, PersonaArtifactRecord>();
  #selectedArtifactId: string | null = null;

  // WebMCP dedupe: keys are `${executionId}:${toolCallId}` so they're
  // naturally scoped to a single dispatch. A later dispatch (new executionId)
  // that happens to recycle a `toolCall.id` never collides with prior entries,
  // and a stale re-emit from an in-flight prior dispatch stays blocked because
  // its executionId is still in the set.
  //
  //   webMcpInflightKeys: currently executing; cleared on completion of
  //                         EITHER /resume success OR /resume throw. Blocks
  //                         concurrent re-fire during the resolve round-trip.
  //   webMcpResolvedKeys: /resume HTTP returned 2xx; not cleared on a new
  //                         dispatch (executionId scoping makes that
  //                         unnecessary). Blocks stale await re-emits
  //                         for the same execution.
  //
  // If `/resume` throws (network error, server 5xx), we DO want a retry path:
  // the dispatch is recoverable. Such a tool stays in neither set, so a
  // subsequent re-emit will re-trigger.
  private webMcpInflightKeys: Set<string> = new Set();
  private webMcpResolvedKeys: Set<string> = new Set();
  // Per-resolve AbortControllers, kept in a set so multiple `webmcp:*`
  // await resolves in one turn never abort one another. The shared
  // `this.abortController` is intentionally NOT used by resolveWebMcpToolCall:
  // in a CHAINED turn (tool A → /resume → tool B, where the server emits B's
  // await inside A's resume SSE stream) the shared controller is still
  // piping A's resume stream: the very stream that just delivered B. Aborting
  // it mid-chain (the prior shared-controller pre-abort) tore that stream down,
  // so B never reached execute() and its /resume was never POSTed, pausing the
  // dispatch forever. cancel(), clearMessages(), hydrateMessages(), and
  // sendMessage() iterate this set to tear every in-flight resolve down on a
  // real stop / new turn.
  private webMcpResolveControllers: Set<AbortController> = new Set();
  // Bumped on every teardown / new-turn boundary (cancel, clearMessages,
  // hydrateMessages, sendMessage). A resolveWebMcpToolCall deferred via
  // queueMicrotask captures the epoch at queue time and bails if it changed,
  // so a resolve queued just before a teardown can't escape it by installing a
  // fresh controller after the set was already cleared.
  #webMcpEpoch = 0;
  // WebMCP native approval-bubble gate. When no custom `webmcp.onConfirm` is
  // supplied, the bridge's confirm handler routes here: we inject an
  // approval-variant message and park the bridge on a Promise that resolves
  // when the user clicks Approve/Deny (see requestWebMcpApproval /
  // resolveWebMcpApproval). Resolvers are keyed by the approval message id.
  private webMcpApprovalResolvers: Map<string, (approved: boolean) => void> =
    new Map();
  #webMcpApprovalSeq = 0;
  // Per approval card: the token of its latest in-flight decision request.
  // Only that request's failure may reopen the card (two decisions can share
  // a Date.now()). Each entry is dropped once its request settles.
  private approvalTokens = new Map<string, object>();
  // Parallel local-tool batching (core#3878). A single model turn can emit
  // multiple `await(local_tool_required)` events for ONE paused
  // executionId: including two PARALLEL calls to the SAME tool ("add SHOE-001
  // and SHOE-007"). Those collapse to an identical `toolId`/`index` and differ
  // only by the per-call `webMcpToolCallId`. We collect all awaits for an
  // executionId that arrive in the same stream tick, then post ONE `/resume`
  // keyed by `webMcpToolCallId`: NOT one `/resume` per tool keyed by name
  // (which collides for same-tool calls, and whose concurrent posts on one
  // execution raced → the second 404'd → the turn hung). Keyed by executionId;
  // `seen` dedupes duplicate await re-emits within a batch. Cleared on
  // every teardown via `abortWebMcpResolves`.
  private webMcpAwaitBatches: Map<
    string,
    { snapshots: AgentWidgetMessage[]; seen: Set<string> }
  > = new Map();

  // Voice support
  #voiceProvider: VoiceProvider | null = null;
  /** In-flight setupVoice chunk load; toggleVoice awaits it when racing setup. */
  #voiceSetupPromise: Promise<void> | null = null;
  /** Bumped by setupVoice/cleanupVoice so a stale chunk load can't install. */
  #voiceSetupGeneration = 0;
  /** Latest 0..1 capture amplitude pushed by a provider that reports one. */
  #voiceLevel = 0;
  #voiceActive = false;
  #voiceStatus: VoiceStatus = 'disconnected';

  // ── Visitor conversation history (D5) ──────────────────────────
  // Identity of the ONE currently open record; not a conversation→session map.
  #activeConversationId: string | null = null;
  #activeConversationRevision: string | null = null;
  #historyNextMessageCursor: string | null = null;
  /** Monotonic: a later selection supersedes every earlier fetch/prepare. */
  #historyOpenEpoch = 0;
  #historyContextEpoch = 0;
  #activeHistoryActivationContext: number | null = null;
  #historyActivationTail: Promise<void> = Promise.resolve();
  /** Blocks `setClientSession`'s welcome branch across a reopen re-init. */
  #suppressWelcomeInjection = false;
  #historyRecovery: "new_conversation_required" | null = null;
  #historySendBlocked = false;
  /** Continuity wipe / boot reconciliation; dispatch waits on it. */
  #historyGate: Promise<void> | null = null;
  /** Deduped by `(id, cursor)` so repeated clicks issue one request. */
  #olderPageRequests = new Map<string, Promise<HistoryOlderPage>>();
  /** Serializes projection finalization against every other operation. */
  #projectionFinalizationPromise: Promise<void> | null = null;
  /** Message id → projection the server has acknowledged (sent or finalized). */
  #acknowledgedProjections = new Map<string, string>();
  #pendingProjections: PendingDisplayProjections | null = null;
  /** Persisted revision as it stood before boot init overwrote it. */
  #bootConversationRevision: string | null = null;
  #bootRevisionCaptured = false;
  /** Exactly one visitor-store subscription per instance; re-keyed with the store. */
  #visitorStoreUnsubscribe: (() => void) | null = null;
  #subscribedVisitorStore: VisitorStore | null = null;
  /** An external credential change happened; the next dispatch must re-init. */
  #credentialReinitPending = false;
  #credentialReinitPromise: Promise<void> | null = null;

  constructor(
    private config: AgentWidgetConfig = {},
    private callbacks: SessionCallbacks,
    private historyInternals: WidgetHistoryInternals = {}
  ) {
    this.historyInternals = this.#composeHistoryInternals(historyInternals);
    this.messages = [...(config.initialMessages ?? [])].map((message) => ({
      ...restoreBrowserToolState(message),
      sequence: message.sequence ?? this.#nextSequence()
    }));
    this.messages = this.#sortMessages(this.messages);
    this.client = new AgentWidgetClient(config, this.historyInternals);
    this.#syncVisitorStoreSubscription();
    this.#wireDefaultWebMcpConfirm();

    // Hydrate artifacts from config (mirrors `initialMessages`). Restored
    // records are forced to `status: "complete"`: a mid-stream artifact should
    // never reappear after a refresh with its skeleton still showing.
    for (const rec of config.initialArtifacts ?? []) {
      this.artifacts.set(rec.id, { ...rec, status: "complete" });
    }
    if (config.initialSelectedArtifactId != null) {
      this.#selectedArtifactId = config.initialSelectedArtifactId;
    }

    if (this.messages.length) {
      this.callbacks.onMessagesChanged([...this.messages]);
    }
    if (this.artifacts.size > 0) {
      this.#emitArtifactsState();
    }
    this.callbacks.onStatusChanged(this.status);
    this.#prefetchRuntypeTts();
    this.#prefetchVoiceRuntime();
  }

  /**
   * Warm the deferred voice-runtime chunk (provider factory + providers) at
   * init when a voice provider is configured, so setupVoice / the first mic
   * click resolve against a warm module. Module fetch only — WebSocket and
   * AudioContext still wait for their own triggers. Errors are swallowed: the
   * setupVoice path retries via the chunk loader's rejection-retry.
   */
  #prefetchVoiceRuntime(): void {
    if (!this.config.voiceRecognition?.provider) return;
    void loadVoiceRuntime().catch(() => {});
  }

  /**
   * Warm the deferred Runtype TTS engine chunk at init so it's resolved before
   * the first "Read aloud" click. Module fetch only — no AudioContext (that
   * still waits for the user gesture on first playback). In the IIFE/CDN build
   * this fetches the standalone `runtype-tts.js`; in bundler builds the module
   * is already inlined, so this is a cheap no-op. Errors are swallowed: a failed
   * prefetch just means the chunk loads on first click instead (or, on a hard
   * failure, the browser-voice fallback kicks in).
   */
  #prefetchRuntypeTts(): void {
    const tts = this.config.textToSpeech;
    if (tts?.provider !== "runtype" || tts.createEngine) return;
    // Only warm the chunk when the engine will actually be built (same gate as
    // createSpeechEngine) — an incomplete config (e.g. a proxy demo with no
    // clientToken) falls back to the browser voice, so the fetch would be wasted.
    const host = tts.host ?? this.config.apiUrl;
    const agentId =
      tts.agentId ?? this.config.voiceRecognition?.provider?.runtype?.agentId ?? this.config.agentId;
    if (!host || !agentId || !this.config.clientToken) return;
    void loadRuntypeTts().catch(() => {});
  }

  /**
   * Set callback for capturing raw SSE events (forwards to client)
   */
  public setSSEEventCallback(callback: SSEEventCallback): void {
    this.client.setSSEEventCallback(callback);
  }

  /**
   * Check if running in client token mode
   */
  public isClientTokenMode(): boolean {
    return this.client.isClientTokenMode();
  }

  /**
   * Check if running in agent execution mode
   */
  public isAgentMode(): boolean {
    return this.client.isAgentMode();
  }

  /**
   * Get current agent execution state (if in agent mode)
   */
  public getAgentExecution(): AgentExecutionState | null {
    return this.agentExecution;
  }

  /**
   * Check if an agent execution is currently running
   */
  public isAgentExecuting(): boolean {
    return this.agentExecution?.status === 'running';
  }

  /**
   * Check if voice is supported
   */
  public isVoiceSupported(): boolean {
    return isVoiceSupportedProbe(this.config.voiceRecognition?.provider);
  }

  /**
   * Check if voice is currently active
   */
  public isVoiceActive(): boolean {
    return this.#voiceActive;
  }

  /**
   * Get current voice status
   */
  public getVoiceStatus(): VoiceStatus {
    return this.#voiceStatus;
  }

  /** The voice call's pending status-line notice (why it ended, or reconnecting), once. */
  public getVoiceNotice(): string | null {
    return this.#voiceProvider?.takeNotice?.() ?? null;
  }

  /** The live call's AI-disclosure notice, if it shows one. */
  public getVoiceDisclosure(): string | null {
    return this.#voiceProvider?.getDisclosure?.() ?? null;
  }

  /**
   * Get the voice interruption mode from the provider (none/cancel/barge-in)
   */
  /**
   * Latest capture amplitude (0..1) from the active provider, or null when the
   * provider exposes none. The Web Speech paths have no audio stream, so they
   * always report null and the UI falls back to a fixed midpoint.
   */
  public getVoiceLevel(): number | null {
    return this.#voiceProvider?.onLevel ? this.#voiceLevel : null;
  }

  public getVoiceInterruptionMode(): "none" | "cancel" | "barge-in" {
    if (this.#voiceProvider?.getInterruptionMode) {
      return this.#voiceProvider.getInterruptionMode();
    }
    return "none";
  }

  /**
   * Stop voice playback / cancel in-flight request without starting recording.
   * Returns to idle state.
   */
  public stopVoicePlayback(): void {
    if (this.#voiceProvider?.stopPlayback) {
      this.#voiceTurns.cancel();
      this.#keyedVoice?.cancel();
      this.#voiceProvider.stopPlayback();
    }
  }

  /** Returns true if the barge-in mic stream is alive (hot mic between turns) */
  public isBargeInActive(): boolean {
    return this.#voiceProvider?.isBargeInActive?.() ?? false;
  }

  /** Tear down the barge-in mic pipeline: "hang up" the always-on mic */
  public async deactivateBargeIn(): Promise<void> {
    if (this.#voiceProvider?.deactivateBargeIn) {
      await this.#voiceProvider.deactivateBargeIn();
    }
  }

  // Pending placeholder IDs for Runtype two-phase voice flow
  #pendingVoiceUserMessageId: string | null = null;
  #pendingVoiceAssistantMessageId: string | null = null;
  #voiceTurns = new VoiceTurnTracker();
  #voiceDisconnectPromise: Promise<void> = Promise.resolve();
  // Turn-keyed (full-duplex) transcript reconciler, from the lazy voice runtime.
  #keyedVoice: KeyedVoiceTranscript | null = null;
  // The delegated voice turn running through sendMessage: the assistant
  // messages it streams, and whether it failed or was aborted.
  #voiceDelegation: VoiceDelegationCapture | null = null;


  // Track message IDs where the Runtype provider already played TTS audio
  // so browser TTS doesn't double-speak them
  private ttsSpokenMessageIds = new Set<string>();

  // Owns the per-message "Read aloud" action and the auto-speak path: which
  // message is active, its play/pause state, and the speech engine. The engine
  // is resolved lazily on first playback (inside the user gesture) so a hosted
  // engine via `textToSpeech.createEngine` plugs in without changing this class.
  private readAloud = new ReadAloudController(() => this.#createSpeechEngine());

  /**
   * Resolve the speech engine behind read-aloud (and auto-speak):
   *   1. An explicit `textToSpeech.createEngine` always wins (full BYO control).
   *   2. `provider: 'runtype'` builds the hosted {@link RuntypeSpeechEngine}
   *      from the widget config (host = `apiUrl`, `agentId`, `clientToken`),
   *      wrapped in a browser {@link FallbackSpeechEngine} unless
   *      `browserFallback: false` — so a missing endpoint or transient failure
   *      degrades to the browser voice instead of erroring, and auto-upgrades to
   *      Runtype voices the day the endpoint ships.
   *   3. Otherwise the browser Web Speech API engine.
   */
  #createSpeechEngine(): SpeechEngine | Promise<SpeechEngine> | null {
    const tts = this.config.textToSpeech;
    if (tts?.createEngine) return tts.createEngine();

    const browser = BrowserSpeechEngine.isSupported()
      ? new BrowserSpeechEngine({ pickVoice: tts?.pickVoice })
      : null;

    if (tts?.provider === "runtype") {
      const host = tts.host ?? this.config.apiUrl;
      const agentId =
        tts.agentId ?? this.config.voiceRecognition?.provider?.runtype?.agentId ?? this.config.agentId;
      const clientToken = this.config.clientToken;
      const wantFallback = tts.browserFallback !== false;

      if (host && agentId && clientToken) {
        // Lazy-load the Runtype engine through the deferred loader: the IIFE/CDN
        // build serves it from the standalone `runtype-tts.js` chunk (kept out of
        // `index.global.js`), while bundler consumers get it inlined. Resolved on
        // first playback (inside the user gesture); `createSpeechEngine` may
        // return a Promise. Prefetched at init (see `prefetchRuntypeTts`) so the
        // chunk is warm before the first click.
        return loadRuntypeTts().then(
          ({ RuntypeSpeechEngine, FallbackSpeechEngine }) => {
            const runtype = new RuntypeSpeechEngine({
              host,
              agentId,
              clientToken,
              voice: tts.voice,
              prebufferMs: tts.prebufferMs,
              createPlaybackEngine: tts.createPlaybackEngine,
            });
            return wantFallback && browser
              ? new FallbackSpeechEngine(runtype, browser, {
                  onFallback: (error) =>
                    console.warn(
                      `[persona] Runtype read-aloud failed; using browser voice. ${error.message}`,
                    ),
                })
              : runtype;
          },
        );
      }

      // Runtype was requested but the config is incomplete (no agentId/token —
      // e.g. a proxy-backed demo). Never break the button: use the browser
      // voice. Only warn when intent is clear (a clientToken is present) so a
      // plain proxy demo stays quiet.
      if (wantFallback && browser) {
        if (clientToken) {
          console.warn(
            "[persona] textToSpeech.provider 'runtype' is missing an agentId; using the browser voice. Set textToSpeech.agentId (or voiceRecognition.provider.runtype.agentId).",
          );
        }
        return browser;
      }
    }

    return browser;
  }

  /**
   * Setup voice recognition with the given configuration
   */
  public setupVoice(config?: VoiceConfig) {
    try {
      const voiceConfig = config || this.getVoiceConfigFromConfig();
      if (!voiceConfig) {
        throw new Error('Voice configuration not provided');
      }

      this.cleanupVoice();
      const disconnected = this.#voiceDisconnectPromise;

      // The provider runtime ships in the lazy voice-runtime chunk. The sync
      // signature is preserved: construction + wiring + connect() happen when
      // the chunk resolves (prefetched at session construction when a
      // provider is configured, so this is warm in practice). A stale
      // resolution (cleanupVoice or a newer setupVoice ran meanwhile) is
      // discarded via the generation token.
      const generation = ++this.#voiceSetupGeneration;
      this.#voiceSetupPromise = loadVoiceRuntime()
        .then(async (mod) => {
          await disconnected;
          if (generation !== this.#voiceSetupGeneration) return;
          this.#wireVoiceProvider(mod, mod.createVoiceProvider(voiceConfig));
        })
        .catch((error) => {
          console.error('Failed to setup voice:', error);
        })
        .finally(() => {
          if (generation === this.#voiceSetupGeneration) {
            this.#voiceSetupPromise = null;
          }
        });
    } catch (error) {
      console.error('Failed to setup voice:', error);
    }
  }

  /**
   * Install a provider: the wiring itself ships in the lazy voice-runtime
   * chunk (`voice/session-voice-wiring.ts`); this hands it the private state.
   */
  #wireVoiceProvider(mod: VoiceRuntimeModule, provider: VoiceProvider): void {
    this.#voiceProvider = provider;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const session = this;
    mod.wireSessionVoice(
      {
        session: this as unknown as VoiceWiringSession,
        provider: () => session.#voiceProvider,
        generation: () => session.#voiceSetupGeneration,
        get keyed() {
          return session.#keyedVoice;
        },
        set keyed(keyed) {
          session.#keyedVoice = keyed;
        },
        get pendingUser() {
          return session.#pendingVoiceUserMessageId;
        },
        set pendingUser(id) {
          session.#pendingVoiceUserMessageId = id;
        },
        get pendingAssistant() {
          return session.#pendingVoiceAssistantMessageId;
        },
        set pendingAssistant(id) {
          session.#pendingVoiceAssistantMessageId = id;
        },
        turns: () => session.#voiceTurns,
        busy: () => session.#chatTurnBusy(),
        setStreaming: (streaming) => session.#setStreaming(streaming),
        setDelegation: (capture) => {
          session.#voiceDelegation = capture;
        },
        setLevel: (level) => {
          session.#voiceLevel = level;
        },
        setStatus: (status) => {
          session.#voiceStatus = status;
          session.#voiceActive = status === 'listening';
        },
        settlePending: (final) => session.#settlePendingVoiceTurn(final),
      },
      provider,
      mod.KeyedVoiceTranscript,
      mod.createVoiceSessionBridge
    );
  }

  /**
   * Toggle voice recognition on/off
   */
  public async toggleVoice() {
    const generation = this.#voiceSetupGeneration;
    // A click can race the setup chunk load; wait for it rather than
    // erroring (prefetch at construction makes this window ~one microtask).
    if (!this.#voiceProvider && this.#voiceSetupPromise) {
      await this.#voiceSetupPromise;
    }
    // A navigation or config replacement supersedes a click waiting on setup.
    if (generation !== this.#voiceSetupGeneration) return;
    if (!this.#voiceProvider) {
      console.error('Voice not configured');
      return;
    }

    if (this.#voiceActive) {
      await this.#voiceProvider.stopListening();
    } else {
      // Stop any in-progress TTS so the mic doesn't pick it up
      this.stopSpeaking();
      try {
        await this.#voiceProvider.startListening();
      } catch (error) {
        console.error('Failed to start voice:', error);
      }
    }
  }

  /**
   * Warm the voice path ahead of the mic click (`voiceRecognition.prewarm`).
   * Session-owned providers (`runtype`, `custom`) get `provider.prewarm()` once
   * the lazy voice runtime has installed them; the browser (Web Speech) path
   * warms the client-token session instead. Fire-and-forget and silent.
   */
  public prewarmVoice(): void {
    if (!usesSessionVoice(this.config.voiceRecognition?.provider)) {
      this.warmClientSession();
      return;
    }
    const generation = this.#voiceSetupGeneration;
    const run = () => {
      const provider = this.#voiceProvider;
      if (!provider?.prewarm || generation !== this.#voiceSetupGeneration) return;
      if (this.#voiceActive || this.isBargeInActive()) return;
      void Promise.resolve()
        .then(() => provider.prewarm?.())
        .catch(() => {});
    };
    if (!this.#voiceProvider && this.#voiceSetupPromise) {
      void this.#voiceSetupPromise.then(run, () => {});
      return;
    }
    run();
  }

  /** A chat turn is streaming, resuming, or running local (WebMCP) tools. */
  #chatTurnBusy(): boolean {
    return (
      !!this.abortController ||
      this.webMcpResolveControllers.size > 0 ||
      this.webMcpAwaitBatches.size > 0 ||
      this.#reconnecting ||
      this.status === 'resuming'
    );
  }

  /**
   * Cleanup voice resources
   */
  public cleanupVoice() {
    const notifyDisconnected = this.#voiceProvider !== null;
    this.#voiceSetupGeneration++;
    this.#voiceSetupPromise = null;
    if (this.#voiceProvider) {
      const provider = this.#voiceProvider;
      this.#voiceProvider = null;
      this.#voiceDisconnectPromise = this.#disconnectVoice(provider);
    }
    this.#voiceActive = false;
    this.#voiceStatus = 'disconnected';
    this.#settlePendingVoiceTurn(true);
    this.#keyedVoice?.settle();
    this.#keyedVoice = null;
    this.#voiceTurns = new VoiceTurnTracker();
    if (notifyDisconnected) this.callbacks.onVoiceStatusChanged?.('disconnected');
  }

  async #disconnectVoice(provider: VoiceProvider): Promise<void> {
    try {
      await provider.disconnect();
    } catch (error) {
      console.error('Failed to disconnect voice:', error);
    }
  }

  #settlePendingVoiceTurn(includeUser: boolean): void {
    const userId = includeUser ? this.#pendingVoiceUserMessageId : null;
    const assistantId = this.#pendingVoiceAssistantMessageId;
    if (!userId && !assistantId) return;
    if (assistantId) this.#voiceTurns.cancel();
    if (includeUser) this.#pendingVoiceUserMessageId = null;
    this.#pendingVoiceAssistantMessageId = null;
    if (assistantId) this.ttsSpokenMessageIds.add(assistantId);
    this.messages = this.messages.flatMap((message) => {
      if (message.id !== userId && message.id !== assistantId) return [message];
      if (message.id === assistantId && !message.content.trim()) return [];
      return [{ ...message, streaming: false, voiceProcessing: false }];
    });
    this.callbacks.onMessagesChanged([...this.messages]);
    if (assistantId) this.#setStreaming(false);
  }

  /**
   * Extract voice configuration from widget config
   */
  private getVoiceConfigFromConfig(): VoiceConfig | undefined {
    if (!this.config.voiceRecognition?.provider) {
      return undefined;
    }
    
    const providerConfig = this.config.voiceRecognition.provider;
    
    switch (providerConfig.type) {
      case 'runtype':
        return {
          type: 'runtype',
          runtype: {
            agentId: providerConfig.runtype?.agentId ?? this.config.agentId ?? '',
            // Default credentials/endpoint from the widget config so the minimum
            // voice config collapses to just `agentId`.
            clientToken: providerConfig.runtype?.clientToken ?? this.config.clientToken,
            host: providerConfig.runtype?.host ?? this.config.apiUrl,
            voiceId: providerConfig.runtype?.voiceId,
            createPlaybackEngine: providerConfig.runtype?.createPlaybackEngine,
            prewarmMode: providerConfig.runtype?.prewarmMode,
            attachIdleMs: providerConfig.runtype?.attachIdleMs,
            clientDelegation: providerConfig.runtype?.clientDelegation,
            callContext: providerConfig.runtype?.callContext,
            approvalTimeoutMs: providerConfig.runtype?.approvalTimeoutMs,
            disclosureText: providerConfig.runtype?.disclosureText
          }
        };
      
      case 'browser':
        return {
          type: 'browser',
          browser: {
            language: providerConfig.browser?.language || 'en-US',
            continuous: providerConfig.browser?.continuous
          }
        };

      case 'custom':
        // Bring-your-own provider: pass the instance/factory straight through
        // to the factory, which resolves and validates it.
        return {
          type: 'custom',
          custom: providerConfig.custom
        };

      default:
        return undefined;
    }
  }

  /**
   * Initialize the client session (for client token mode).
   * This is called automatically on first message, but can be called
   * explicitly to pre-initialize the session and get config from server.
   */
  public async initClientSession(): Promise<ClientSession | null> {
    if (!this.isClientTokenMode()) {
      return null;
    }

    // Belt-and-braces with the client-side gate: no init request may observe
    // stored ids before the controller has applied restored state.
    if (this.config.features?.history?.enabled) {
      await this.historyInternals.historyBootstrapReady;
      // Snapshot before init overwrites it: boot reconciliation compares the
      // init revision against what the LAST page load persisted.
      this.#bootConversationRevision =
        this.historyInternals.getStoredConversationRevision?.() ?? null;
      this.#bootRevisionCaptured = true;
    }

    try {
      const session = await this.client.initSession();
      this.setClientSession(session);
      return session;
    } catch (error) {
      this.callbacks.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
      return null;
    }
  }

  /**
   * Early, fire-and-forget client-token init (`sessionInit` trigger or
   * `controller.initSession()`). Failures stay silent here: no `onError`, no
   * UI. The send re-runs `initSession()` and reports them as it always has.
   */
  public warmClientSession(): void {
    // The visitor may send before this resolves; the server welcome still
    // belongs ahead of that first turn when the transcript started empty.
    const pristine = !this.messages.length;
    void this.client.warmSession().then((session) => {
      // A start-new or credential re-init may have replaced it meanwhile.
      if (session && this.client.getClientSession() === session) {
        this.setClientSession(session, pristine);
      }
    });
  }

  /**
   * Set the client session after initialization
   */
  public setClientSession(session: ClientSession, pristine?: boolean): void {
    this.clientSession = session;
    this.#injectWelcomeMessage(session, pristine);
  }

  /**
   * Welcome injection is skipped while `suppressWelcomeInjection` is latched:
   * reopening a stored conversation is not a new thread, and the hydrated
   * transcript would otherwise race a welcome bubble.
   */
  #injectWelcomeMessage(
    session: ClientSession,
    pristine = !this.messages.length
  ): void {
    if (this.#suppressWelcomeInjection) return;
    if (!session.config.welcomeMessage || !pristine) return;
    // A late welcome (the first send raced the init) sorts ahead of that turn.
    const first = this.messages[0];
    const welcomeMessage: AgentWidgetMessage = {
      id: `welcome-${Date.now()}`,
      role: "assistant",
      content: session.config.welcomeMessage,
      createdAt: first?.createdAt ?? new Date().toISOString(),
      sequence: first ? (first.sequence ?? 0) - 1 : this.#nextSequence()
    };
    this.#appendMessage(welcomeMessage);
  }

  /**
   * Get current client session
   */
  public getClientSession(): ClientSession | null {
    return this.clientSession ?? this.client.getClientSession();
  }

  /**
   * Check if session is valid and not expired
   */
  public isSessionValid(): boolean {
    const session = this.getClientSession();
    if (!session) return false;
    return new Date() < session.expiresAt;
  }

  /**
   * Clear session (on expiry or error)
   */
  public clearClientSession(): void {
    this.clientSession = null;
    this.client.clearClientSession();
  }

  /**
   * Get the underlying client instance (for advanced use cases like feedback)
   */
  public getClient(): AgentWidgetClient {
    return this.client;
  }

  /**
   * Submit message feedback (upvote, downvote, or copy) to the API.
   * Only available in client token mode.
   * 
   * @param messageId - The ID of the message to provide feedback for
   * @param type - The feedback type: 'upvote', 'downvote', or 'copy'
   */
  public async submitMessageFeedback(
    messageId: string,
    type: 'upvote' | 'downvote' | 'copy'
  ): Promise<void> {
    return this.client.submitMessageFeedback(messageId, type);
  }

  /**
   * Submit CSAT (Customer Satisfaction) feedback to the API.
   * Only available in client token mode.
   * 
   * @param rating - Rating from 1 to 5
   * @param comment - Optional comment
   */
  public async submitCSATFeedback(rating: number, comment?: string): Promise<void> {
    return this.client.submitCSATFeedback(rating, comment);
  }

  /**
   * Submit NPS (Net Promoter Score) feedback to the API.
   * Only available in client token mode.
   * 
   * @param rating - Rating from 0 to 10
   * @param comment - Optional comment
   */
  public async submitNPSFeedback(rating: number, comment?: string): Promise<void> {
    return this.client.submitNPSFeedback(rating, comment);
  }

  /**
   * Re-thread the controller-owned history dependencies. Called before
   * `updateConfig` when the visitor-store identity tuple changed, so the live
   * client and any rebuilt one share the same store.
   */
  public setHistoryInternals(internals: WidgetHistoryInternals): void {
    if (internals.historyProvider !== this.historyInternals.historyProvider) {
      this.#historyOpenEpoch += 1;
      this.#historyContextEpoch += 1;
      if (this.config.backgroundConversations) this.cancel();
      this.#clearBackgroundConversations();
    }
    this.historyInternals = this.#composeHistoryInternals(internals);
    this.client.setHistoryInternals(this.historyInternals);
    this.#syncVisitorStoreSubscription();
  }

  /** Widget teardown: release the visitor-store subscription. */
  public destroy(): void {
    this.#clearBackgroundConversations();
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.cancel();
    this.#runActivity = undefined;
    this.cleanupVoice();
    this.#visitorStoreUnsubscribe?.();
    this.#visitorStoreUnsubscribe = null;
    this.#subscribedVisitorStore = null;
  }

  // ==========================================================================
  // Visitor conversation history (docs/visitor-history-implementation-plan D5)
  // ==========================================================================

  /**
   * The session always owns the continuity transition, whether or not the shell
   * supplied its own observer: a credential/record break must wipe local state
   * before the replacement session becomes sendable.
   */
  #composeHistoryInternals(
    internals: WidgetHistoryInternals
  ): WidgetHistoryInternals {
    const hostContinuity = internals.onHistoryContinuityChanged;
    return {
      ...internals,
      onHistoryContinuityChanged: (info) => {
        this.#handleHistoryContinuityChanged(info);
        hostContinuity?.(info);
      },
    };
  }

  get #historyProvider(): HistoryProvider | null {
    return this.historyInternals.historyProvider ?? null;
  }

  #requireHistoryProvider(): HistoryProvider {
    const provider = this.#historyProvider;
    if (!provider) {
      throw new SessionHistoryError(
        "history_unavailable",
        "No conversation history provider is installed"
      );
    }
    return provider;
  }

  /**
   * One resolved scope per logical action. An explicitly requested scope the
   * provider cannot serve fails locally rather than silently downgrading.
   */
  #historyContext(scope?: HistoryScope): HistoryOperationContext {
    const provider = this.#requireHistoryProvider();
    const requested = scope ?? this.config.features?.history?.scope;
    const derived: HistoryScope = this.config.getIdentityProof
      ? "verified-user"
      : "browser";
    // Only a requested scope fails closed; the derived default narrows to what
    // the provider advertises.
    const resolved =
      requested ??
      (provider.capabilities.scopes.includes(derived)
        ? derived
        : (provider.capabilities.scopes[0] ?? derived));
    if (!provider.capabilities.scopes.includes(resolved)) {
      throw new HistoryProviderError(
        "unsupported_scope",
        "This history scope is not supported."
      );
    }
    return { scope: resolved };
  }

  public getHistoryState(): SessionHistoryState {
    return {
      activeConversationId: this.#activeConversationId,
      conversationRevision: this.#activeConversationRevision,
      nextMessageCursor: this.#historyNextMessageCursor,
      recovery: this.#historyRecovery,
      sendBlocked: this.#historySendBlocked,
    };
  }

  public getActiveConversationId(): string | null {
    return this.#activeConversationId;
  }

  #emitHistoryState(): void {
    this.callbacks.onHistoryStateChanged?.(this.getHistoryState());
  }

  #notifyHistory(
    code: SessionHistoryNoticeCode,
    message: string
  ): void {
    this.callbacks.onHistoryNotice?.({ code, message });
  }

  /** Pending destructive/reconciliation work every other operation waits on. */
  public async awaitHistorySettled(): Promise<void> {
    const gate = this.#historyGate;
    if (gate) await gate.catch(() => {});
    const finalization = this.#projectionFinalizationPromise;
    if (finalization) await finalization.catch(() => {});
  }

  #assertHistoryIdle(): void {
    if (this.streaming || this.status === "paused" || this.status === "resuming") {
      throw new SessionHistoryError(
        "conversation_busy",
        "Finish or stop the current turn before switching conversations"
      );
    }
  }

  public listConversations(opts?: {
    cursor?: string;
    limit?: number;
    targetId?: string;
    scope?: HistoryScope;
  }) {
    const provider = this.#requireHistoryProvider();
    return provider.list({
      ...(opts?.cursor ? { cursor: opts.cursor } : {}),
      ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      ...(opts?.targetId !== undefined ? { targetId: opts.targetId } : {}),
      context: this.#historyContext(opts?.scope),
    });
  }

  /**
   * Transactional reopen: read the newest page, prepare an authorized
   * activation, then commit only if this selection still wins. Nothing mutates
   * the active conversation before `commit()`.
   */
  public async openConversation(
    id: string,
    opts?: {
      scope?: HistoryScope;
      busy?: "reject" | "stop" | "background";
      /** UI snapshots only after every fallible preparation step succeeds. */
      onBeforeCommit?: () => void;
      /** Runs inside the activation lock, after hydration, including queued opens. */
      onCommitted?: () => void;
    }
  ): Promise<{
    summary: HistoryConversationSummary;
    nextMessageCursor: string | null;
  }> {
    const provider = this.#requireHistoryProvider();
    if (opts?.busy === "background") this.assertBackgroundSwitchable();
    if (opts?.busy === "stop") this.cancel();
    if (opts?.busy !== "background") this.#assertHistoryIdle();
    const context = this.#historyContext(opts?.scope);
    const epoch = ++this.#historyOpenEpoch;
    const contextEpoch = this.#historyContextEpoch;
    if (opts?.busy === "background") await this.#loadBackgroundController();
    await this.awaitHistorySettled();
    if (epoch !== this.#historyOpenEpoch) {
      throw new SessionHistoryError("superseded", "A later selection won");
    }
    const page = await provider.getPage(id, { context });
    if (epoch !== this.#historyOpenEpoch) {
      throw new SessionHistoryError("superseded", "A later selection won");
    }
    const prepared = await provider.prepareOpen(id, { context });
    const activation = this.#historyActivationTail.then(async () => {
      if (epoch !== this.#historyOpenEpoch) {
        prepared.discard();
        throw new SessionHistoryError("superseded", "A later selection won");
      }
      // Headless callers can send while getPage/prepareOpen is pending.
      if (opts?.busy === "stop") this.cancel();
      try {
        if (opts?.busy === "background") this.assertBackgroundSwitchable();
        else this.#assertHistoryIdle();
      } catch (error) { prepared.discard(); throw error; }
      const originId = this.#activeConversationId;
      const originRun = this.#activeRun;
      opts?.onBeforeCommit?.();
      if (opts?.busy === "background") this.#backgroundCommitEvents = [];
      this.#suppressWelcomeInjection = true;
      this.#activeHistoryActivationContext = contextEpoch;
      try {
        await this.#commitActivation(prepared);
        if (contextEpoch !== this.#historyContextEpoch || provider !== this.#historyProvider) {
          throw new SessionHistoryError("superseded", "The history identity changed");
        }
        // Commit is the last fallible step. A failed/superseded preparation
        // therefore never aborts the current transport or loses its checkpoint.
        if (opts?.busy === "background" && originId && this.#resumable) {
          const checkpoint: BackgroundCheckpoint = {
            conversationId: originId,
            handle: this.#resumable,
            messages: this.messages,
            artifacts: this.getArtifacts(),
            selectedArtifactId: this.#selectedArtifactId,
            streamCheckpoint: this.#streamCheckpoint,
          };
          this.#backgroundController?.save(checkpoint);
        }
        this.#activeConversationId = prepared.conversationId || id;
        this.#activeConversationRevision =
          prepared.conversationRevision || page.conversationRevision || null;
        this.#historyNextMessageCursor = page.nextCursor;
        this.#historyRecovery = null;
        this.#historySendBlocked = false;
        this.#resetConversationScopedState();
        for (const message of page.messages) {
          this.#acknowledgedProjections.set(message.id, message.content);
        }
        this.#persistConversationMetadata();
        const checkpoint = this.#backgroundController?.take(this.#activeConversationId);
        // Replay must fill its own saved transcript at the saved cursor. The
        // freshly fetched server history may already include post-cursor output.
        this.hydrateMessages(checkpoint?.messages ?? page.messages);
        if (checkpoint) {
          this.hydrateArtifacts(checkpoint.artifacts, checkpoint.selectedArtifactId, true);
          this.#restoreBackgroundCheckpoint(checkpoint);
        }
        opts?.onCommitted?.();
        this.#emitHistoryState();
        return { summary: page.summary, nextMessageCursor: page.nextCursor };
      } finally {
        this.#suppressWelcomeInjection = false;
        this.#activeHistoryActivationContext = null;
        const buffered = this.#backgroundCommitEvents;
        this.#backgroundCommitEvents = null;
        // Only a failed commit retains the original run. Release its events
        // there; a winning switch replays from its saved cursor on return.
        if (buffered && this.#activeRun === originRun && this.#activeConversationId === originId) {
          for (const { event, readOnlyReplay } of buffered) this.handleEvent(event, readOnlyReplay);
        }
      }
    });
    const gate = activation.then(() => {}, () => {});
    this.#historyActivationTail = gate;
    this.#historyGate = gate;
    try { return await activation; }
    finally { if (this.#historyGate === gate) this.#historyGate = null; }
  }

  /**
   * Prepend one older page. Deduped by `(id, cursor)`; merges by id and repaints
   * once (the `injectMessageBatch` single-render pattern).
   */
  public loadOlderMessages(
    id: string,
    cursor?: string | null,
    opts?: { scope?: HistoryScope }
  ): Promise<HistoryOlderPage> {
    const provider = this.#requireHistoryProvider();
    const context = this.#historyContext(opts?.scope);
    const resolvedCursor = cursor ?? this.#historyNextMessageCursor;
    if (!resolvedCursor) {
      return Promise.resolve({ messages: [], nextMessageCursor: null });
    }
    const key = `${id} ${resolvedCursor}`;
    const inflight = this.#olderPageRequests.get(key);
    if (inflight) return inflight;

    const request = this.#fetchOlderPage(provider, id, resolvedCursor, context)
      .finally(() => {
        this.#olderPageRequests.delete(key);
      });
    this.#olderPageRequests.set(key, request);
    return request;
  }

  async #fetchOlderPage(
    provider: HistoryProvider,
    id: string,
    cursor: string,
    context: HistoryOperationContext
  ): Promise<HistoryOlderPage> {
    const oldest = this.messages[0]?.createdAt;
    const page = await provider.getPage(id, {
      cursor,
      ...(oldest ? { beforeCreatedAt: oldest } : {}),
      context,
    });
    // A conversation switch during the fetch retires this page.
    if (this.#activeConversationId && this.#activeConversationId !== id) {
      return { messages: [], nextMessageCursor: page.nextCursor };
    }
    const restored = this.#restoreHistoryPage(page.messages);
    const merged = mergeWireMessagesById(this.messages, restored);
    this.messages = this.#sortMessages(
      merged.map((message) => this.#ensureSequence(message))
    );
    this.#historyNextMessageCursor = page.nextCursor;
    for (const message of page.messages) {
      this.#acknowledgedProjections.set(message.id, message.content);
    }
    this.historyInternals.setStoredMessageCursor?.(page.nextCursor);
    this.callbacks.onMessagesChanged([...this.messages]);
    this.#emitHistoryState();
    return { messages: restored, nextMessageCursor: page.nextCursor };
  }

  /** Normalize incoming history without retiring callbacks owned by this chat. */
  #restoreHistoryPage(messages: AgentWidgetMessage[]): AgentWidgetMessage[] {
    const current = new Map(this.messages.map(message => [message.id, message]));
    return messages.map(message => {
      const live = current.get(message.id);
      if (live) {
        if (this.webMcpApprovalResolvers.has(live.id)) return live;
        // A decision made while the history fetch was in flight must not be
        // replaced by the page's stale pending copy of the same browser card.
        if (live.approval && isBrowserLocalApproval(live.approval) &&
          live.approval.id === message.approval?.id &&
          live.approval.status !== "pending" && message.approval?.status === "pending") return live;
        const executionId = live.agentMetadata?.executionId;
        const tools = [...(live.toolCall ? [live.toolCall] : []), ...(live.tools ?? [])];
        if (executionId && tools.some(tool =>
          this.webMcpInflightKeys.has(`${executionId}:${tool.id}`) ||
          this.webMcpResolvedKeys.has(`${executionId}:${tool.id}`)
        )) return live;
      }
      return restoreBrowserToolState(message);
    });
  }

  /**
   * The canonical new-thread operation. A failed prepare leaves the current
   * conversation completely intact.
   */
  public async startNewConversation(opts?: {
    scope?: HistoryScope;
    onBeforeCommit?: () => void;
    onCommitted?: () => void;
  }): Promise<void> {
    const provider = this.#requireHistoryProvider();
    const context = this.#historyContext(opts?.scope);
    const epoch = ++this.#historyOpenEpoch;
    const contextEpoch = this.#historyContextEpoch;
    await this.awaitHistorySettled();
    const prepared = await provider.prepareStartNew({ context });
    const activation = this.#historyActivationTail.then(async () => {
      if (epoch !== this.#historyOpenEpoch) {
        prepared.discard();
        throw new SessionHistoryError("superseded", "A later action won");
      }
      opts?.onBeforeCommit?.();
      // Commit may fail: preserve the old transcript until it succeeds.
      this.#suppressWelcomeInjection = true;
      this.#activeHistoryActivationContext = contextEpoch;
      try { await this.#commitActivation(prepared); }
      finally { this.#suppressWelcomeInjection = false; this.#activeHistoryActivationContext = null; }
      if (contextEpoch !== this.#historyContextEpoch || provider !== this.#historyProvider) {
        throw new SessionHistoryError("superseded", "The history identity changed");
      }
      // The committed activation already persisted its replacement session.
      this.#discardConversationState(false, true);
      this.#activeConversationId = prepared.conversationId || null;
      this.#activeConversationRevision = prepared.conversationRevision || null;
      this.#historyNextMessageCursor = null;
      this.#historyRecovery = null;
      this.#historySendBlocked = false;
      this.#persistConversationMetadata();
      if (this.clientSession) this.#injectWelcomeMessage(this.clientSession);
      opts?.onCommitted?.();
      this.#emitHistoryState();
    });
    const gate = activation.then(() => {}, () => {});
    this.#historyActivationTail = gate;
    this.#historyGate = gate;
    try { await activation; }
    finally { if (this.#historyGate === gate) this.#historyGate = null; }
  }

  /**
   * Clear local + persisted conversation state, then commit the replacement.
   * Welcome injection is deliberately allowed once the transcript is empty.
   */
  async #installFreshConversation(
    prepared: PreparedHistoryActivation,
    preserveRun = false
  ): Promise<void> {
    this.#discardConversationState(preserveRun);
    await this.#commitActivation(prepared);
    this.#activeConversationId = prepared.conversationId || null;
    this.#activeConversationRevision = prepared.conversationRevision || null;
    this.#historyNextMessageCursor = null;
    this.#historyRecovery = null;
    this.#historySendBlocked = false;
    this.#persistConversationMetadata();
    this.#emitHistoryState();
  }

  /**
   * Deleting the active record (or everything) is already irreversible: the
   * transcript is destroyed first, then a replacement session is prepared.
   */
  public async deleteConversation(
    id: string,
    opts?: { scope?: HistoryScope }
  ): Promise<void> {
    const provider = this.#requireHistoryProvider();
    const context = this.#historyContext(opts?.scope);
    await this.awaitHistorySettled();
    await provider.delete(id, { context });
    this.#backgroundController?.clear(id);
    if (id !== this.#activeConversationId) return;
    await this.#recoverFromDestroyedConversation(context);
  }

  /** Visitor-scoped rename/star. Requires a provider with the update capability. */
  public async updateConversation(
    id: string,
    patch: HistoryConversationPatch,
    opts?: { scope?: HistoryScope }
  ): Promise<HistoryConversationSummary> {
    const provider = this.#requireHistoryProvider();
    if (!provider.update) {
      throw new SessionHistoryError(
        "history_unavailable",
        "This history provider cannot update conversations"
      );
    }
    const context = this.#historyContext(opts?.scope);
    await this.awaitHistorySettled();
    return provider.update(id, patch, { context });
  }

  /** True when the provider supports visitor-scoped rename/star. */
  public canUpdateConversations(): boolean {
    return typeof this.#historyProvider?.update === "function";
  }

  public async clearConversationHistory(opts?: {
    targetId?: string;
    scope?: HistoryScope;
  }): Promise<{ deleted: number }> {
    const provider = this.#requireHistoryProvider();
    const context = this.#historyContext(opts?.scope);
    await this.awaitHistorySettled();
    const result = await provider.deleteAll({
      ...(opts?.targetId !== undefined ? { targetId: opts.targetId } : {}),
      context,
    });
    this.#clearBackgroundConversations();
    await this.#recoverFromDestroyedConversation(context);
    return result;
  }

  /**
   * Revoke this browser's history credential. Resolves (never rejects) on
   * remote failure; the local wipe is unconditional either way.
   */
  public async resetHistoryDevice(): Promise<{
    remoteRevocationConfirmed: boolean;
  }> {
    const provider = this.#requireHistoryProvider();
    if (!provider.resetDevice) {
      throw new SessionHistoryError(
        "history_unavailable",
        "This history provider cannot reset the device"
      );
    }
    this.#clearBackgroundConversations();
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.#projectionFinalizationPromise = null;
    try {
      return await provider.resetDevice();
    } finally {
      this.#discardConversationState();
      this.#activeConversationId = null;
      this.#activeConversationRevision = null;
      this.#historyNextMessageCursor = null;
      this.#historyRecovery = null;
      this.#historySendBlocked = false;
      this.#emitHistoryState();
    }
  }

  async #recoverFromDestroyedConversation(
    context: HistoryOperationContext
  ): Promise<void> {
    // Supersede anything still in flight for the record being destroyed.
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.#projectionFinalizationPromise = null;
    this.#discardConversationState();
    this.#activeConversationId = null;
    this.#activeConversationRevision = null;
    this.#historyNextMessageCursor = null;
    this.#historySendBlocked = true;
    this.#historyRecovery = null;
    this.#emitHistoryState();
    await this.#prepareReplacementConversation(context);
  }

  /**
   * Never reinstalls the deleted session. A failed preparation leaves an
   * explicit recoverable state instead of the old transcript.
   */
  async #prepareReplacementConversation(
    context: HistoryOperationContext
  ): Promise<void> {
    const provider = this.#historyProvider;
    if (!provider) {
      this.#historySendBlocked = false;
      this.#emitHistoryState();
      return;
    }
    try {
      const prepared = await provider.prepareStartNew({ context });
      await this.#installFreshConversation(prepared);
    } catch {
      this.#historySendBlocked = true;
      this.#historyRecovery = "new_conversation_required";
      this.#emitHistoryState();
      this.#notifyHistory(
        "new_conversation_required",
        "That conversation was deleted. Start a new conversation to continue."
      );
    }
  }

  /** The next explicit action retries a failed replacement preparation. */
  async #retryRequiredConversation(): Promise<void> {
    if (this.#historyRecovery !== "new_conversation_required") return;
    const provider = this.#historyProvider;
    if (!provider) return;
    this.#historyRecovery = null;
    await this.#prepareReplacementConversation(this.#historyContext());
    if (this.#historyRecovery === "new_conversation_required") {
      throw new SessionHistoryError(
        "new_conversation_required",
        "A new conversation is required before sending"
      );
    }
  }

  /**
   * Install a prepared activation. The provider's commit also invokes the
   * session binding (`bindActivatedSession`); the fallback covers a shell that
   * has not wired that callback yet.
   */
  async #commitActivation(
    prepared: PreparedHistoryActivation
  ): Promise<void> {
    await prepared.commit();
    if (this.#activeHistoryActivationContext !== null && this.#activeHistoryActivationContext !== this.#historyContextEpoch) return;
    const installed = this.client.getClientSession();
    if (installed && installed.sessionId !== this.clientSession?.sessionId) {
      this.bindActivatedSession(installed);
    }
  }

  /**
   * Session-owned binding for a winning prepared activation. Installs the
   * client session WITHOUT the ordinary welcome branch while the reopen latch
   * is set, and persists the replacement ids through the normal storage path.
   */
  public bindActivatedSession(session: ClientSession): void {
    if (this.#activeHistoryActivationContext !== null && this.#activeHistoryActivationContext !== this.#historyContextEpoch) return;
    this.clientSession = session;
    this.config.setStoredSessionId?.(session.sessionId);
    if (session.conversationId) {
      this.#activeConversationId = session.conversationId;
      this.#activeConversationRevision = session.conversationRevision ?? null;
      this.config.setStoredConversationId?.(session.conversationId);
      this.historyInternals.setStoredConversationRevision?.(
        session.conversationRevision ?? null
      );
    }
    if (!this.#suppressWelcomeInjection) this.#injectWelcomeMessage(session);
  }

  #persistConversationMetadata(): void {
    if (this.#activeConversationId) {
      this.config.setStoredConversationId?.(this.#activeConversationId);
    } else {
      this.config.clearStoredConversationId?.();
    }
    this.historyInternals.setStoredConversationRevision?.(
      this.#activeConversationRevision
    );
    this.historyInternals.setStoredMessageCursor?.(
      this.#historyNextMessageCursor
    );
  }

  /** Conversation-scoped caches that must not leak across records. */
  #resetConversationScopedState(): void {
    this.#runActivity = undefined;
    this.historyInternals.setStoredResumableHandle?.(null);
    this.agentExecution = null;
    this.#clearArtifactState();
    this.webMcpInflightKeys.clear();
    this.webMcpResolvedKeys.clear();
    this.client.resetClientToolsFingerprint();
    this.#acknowledgedProjections.clear();
    this.#setPendingProjections(null);
  }

  /**
   * Irreversible local + persisted clear. Used by new-conversation, active
   * deletion, 410 recovery, reset, and the continuity wipe.
   */
  #discardConversationState(preserveRun = false, preserveSessionId = false): void {
    this.client.cancelStreams();
    this.stopSpeaking();
    this.abortController?.abort();
    this.abortController = null;
    this.#teardownReconnect();
    this.#abortWebMcpResolves();
    this.#actionsEpoch++;
    this.messages = [];
    this.#resetConversationScopedState();
    this.#olderPageRequests.clear();
    if (!preserveSessionId) this.config.clearStoredSessionId?.();
    this.config.clearStoredConversationId?.();
    this.historyInternals.setStoredConversationRevision?.(null);
    this.historyInternals.setStoredMessageCursor?.(null);
    if (!preserveRun) this.#setStreaming(false);
    this.#setStatus("idle");
    this.callbacks.onMessagesChanged([...this.messages]);
    if (!preserveRun) this.#finishRun("cancel");
  }

  /**
   * D6 continuity wipe (session side): a replacement visitor, identity rebind,
   * cross-tab convergence, or credential expiry selected a different record.
   * Invalidate synchronously; dispatch stays blocked until cleanup completes.
   */
  #handleHistoryContinuityChanged(info: {
    previousConversationId: string | null;
    conversationId: string;
  }): void {
    this.#clearBackgroundConversations();
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.#projectionFinalizationPromise = null;
    this.#historySendBlocked = true;
    this.#discardConversationState();
    this.#activeConversationId = info.conversationId;
    this.#activeConversationRevision = null;
    this.#historyNextMessageCursor = null;
    this.#historyRecovery = null;
    this.#emitHistoryState();
    this.#notifyHistory(
      "history_continuity_reset",
      "This browser was reconnected to a different conversation, so the previous messages were cleared."
    );
    this.#historyGate = Promise.resolve().then(() => {
      this.#historySendBlocked = false;
      this.#historyGate = null;
      this.#emitHistoryState();
    });
  }

  /**
   * One subscription per widget instance, re-keyed when the controller swaps
   * the store. Owned here so the transition sits beside the continuity wipe it
   * reuses.
   */
  #syncVisitorStoreSubscription(): void {
    const store = this.historyInternals.visitorStore ?? null;
    if (store === this.#subscribedVisitorStore) return;
    this.#visitorStoreUnsubscribe?.();
    this.#visitorStoreUnsubscribe = null;
    this.#subscribedVisitorStore = store;
    if (!store) return;
    this.#visitorStoreUnsubscribe = store.subscribe((change) =>
      this.#handleExternalCredentialChange(change)
    );
  }

  /**
   * D3 privacy boundary: a sibling tab cleared or replaced the shared visitor
   * credential. Privacy wins over turn continuity, so a live turn is cancelled.
   * A "local" change is this tab's own mint/write and owns its init path; a
   * change out of an empty store is first-init convergence, not a break.
   */
  #handleExternalCredentialChange(change: VisitorStoreChange): void {
    if (change.source !== "external" || change.previousToken === null) return;
    this.#clearBackgroundConversations();

    // Nothing may reuse the session minted under the revoked credential.
    this.clientSession = null;
    this.client.handleExternalCredentialChange();
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.#projectionFinalizationPromise = null;
    this.#credentialReinitPending = true;
    this.#credentialReinitPromise = null;

    const hadVisibleTranscript = this.messages.length > 0;
    const hadConversationState =
      hadVisibleTranscript ||
      this.#activeConversationId !== null ||
      (this.config.getStoredConversationId?.() ?? null) !== null;
    if (hadConversationState) {
      this.#discardConversationState();
    } else {
      // No conversation to wipe, but the revoked visitor's ids must not survive.
      this.config.clearStoredSessionId?.();
      this.config.clearStoredConversationId?.();
      this.#olderPageRequests.clear();
      this.#setPendingProjections(null);
    }
    this.#activeConversationId = null;
    this.#activeConversationRevision = null;
    this.#historyNextMessageCursor = null;
    this.#historyRecovery = null;
    this.#emitHistoryState();
    this.callbacks.onHistoryScopeInvalidated?.();
    // Only a visible wipe announces: a silent reconcile must not push the open
    // history view into a refetch that would immediately re-mint a credential.
    if (hadVisibleTranscript) {
      this.#notifyHistory(
        "history_continuity_reset",
        "This browser's conversation history was reset in another tab, so the previous messages were cleared."
      );
    }
  }

  /**
   * Dispatch may not proceed until a session exists under the NEW credential.
   * Deliberately lazy: re-initializing on the storage event itself would hand
   * an idle tab a fresh credential the visitor just deleted elsewhere.
   */
  #completeCredentialReinit(): Promise<void> {
    if (!this.#credentialReinitPending) return Promise.resolve();
    if (!this.#credentialReinitPromise) {
      this.#credentialReinitPromise = this.#runCredentialReinit().finally(() => {
        this.#credentialReinitPromise = null;
      });
    }
    return this.#credentialReinitPromise;
  }

  async #runCredentialReinit(): Promise<void> {
    if (!this.isClientTokenMode()) {
      this.#credentialReinitPending = false;
      return;
    }
    try {
      // Reads the store again: a clear mints fresh, a replacement adopts the
      // sibling's token.
      this.bindActivatedSession(await this.client.initSession());
      this.#credentialReinitPending = false;
    } catch {
      // Still pending: the dispatch's own init reports the failure normally.
    }
  }

  /**
   * Authoritative boot reconciliation (D4). Runs after a `conversationId` boot
   * resume and before chat becomes sendable: an unchanged revision needs no
   * detail fetch; a changed or absent one refreshes from the server's newest
   * page under the same scope.
   */
  public reconcileBootConversation(opts?: {
    scope?: HistoryScope;
  }): Promise<void> {
    const provider = this.#historyProvider;
    const conversationId =
      this.clientSession?.conversationId ??
      this.config.getStoredConversationId?.() ??
      this.#activeConversationId;
    if (!provider || !conversationId) return Promise.resolve();

    const context = this.#historyContext(opts?.scope);
    this.#historySendBlocked = true;
    this.#emitHistoryState();
    const run = this.#runBootReconciliation(
      provider,
      conversationId,
      context
    ).finally(() => {
      this.#historySendBlocked = this.#historyRecovery !== null;
      if (this.#historyGate === run) this.#historyGate = null;
      this.#emitHistoryState();
    });
    this.#historyGate = run;
    return run;
  }

  async #runBootReconciliation(
    provider: HistoryProvider,
    conversationId: string,
    context: HistoryOperationContext
  ): Promise<void> {
    this.#activeConversationId = conversationId;
    const initRevision =
      this.clientSession?.conversationRevision ??
      this.#activeConversationRevision ??
      null;
    const persistedRevision = this.#bootRevisionCaptured
      ? this.#bootConversationRevision
      : (this.historyInternals.getStoredConversationRevision?.() ?? null);
    this.#bootRevisionCaptured = false;
    this.#historyNextMessageCursor =
      this.historyInternals.getStoredMessageCursor?.() ??
      this.#historyNextMessageCursor;

    // Any locally finalized projection still owed to the server goes first, so
    // display reconciliation cannot complete against a stale server record.
    await this.replayPendingProjections();

    if (initRevision && persistedRevision && initRevision === persistedRevision) {
      this.#activeConversationRevision = initRevision;
      return;
    }

    const page = await provider.getPage(conversationId, { context });
    const restored = this.#restoreHistoryPage(page.messages);
    const serverIds = new Set(page.messages.map((message) => message.id));
    const overlaps = this.messages.some((message) => serverIds.has(message.id));
    const pageOldest = page.messages[0]?.createdAt;

    let reconciled: AgentWidgetMessage[];
    if (!overlaps) {
      // No shared id: the local transcript is not this record's newest window.
      reconciled = restored.map((message) => this.#ensureSequence(message));
    } else {
      const durableId = this.#resumable ? this.#activeAssistantMessageId : null;
      const survivors = this.messages.filter((message) => {
        if (serverIds.has(message.id)) return true;
        if (message.id === durableId) return true;
        // Older than the server window is legitimately absent from this page.
        return pageOldest !== undefined && message.createdAt < pageOldest;
      });
      reconciled = mergeWireMessagesById(survivors, restored).map(
        (message) => this.#ensureSequence(message)
      );
    }

    this.#historyNextMessageCursor = page.nextCursor;
    this.#activeConversationRevision =
      page.conversationRevision || initRevision || null;
    this.#acknowledgedProjections.clear();
    for (const message of page.messages) {
      this.#acknowledgedProjections.set(message.id, message.content);
    }
    // Metadata first, transcript last: one persist covers both.
    this.#persistConversationMetadata();
    this.#runActivity = undefined;
    this.messages = this.#sortMessages(reconciled);
    this.callbacks.onMessagesChanged([...this.messages]);
  }

  // ── Display-projection finalization ────────────────────────────

  /** Runtype transport op: active session + browser scope, never a proof. */
  #projectionFinalizationEnabled(): boolean {
    return (
      this.config.features?.history?.enabled === true &&
      this.isClientTokenMode()
    );
  }

  #setPendingProjections(pending: PendingDisplayProjections | null): void {
    this.#pendingProjections = pending;
    // Memory-only when the shell supplies no persistence seam.
    this.historyInternals.setPendingDisplayProjections?.(pending);
  }

  #readPendingProjections(): PendingDisplayProjections | null {
    return (
      this.historyInternals.getPendingDisplayProjections?.() ??
      this.#pendingProjections
    );
  }

  /** Record what the dispatch payload carried as each message's projection. */
  #recordDispatchedProjections(messages: AgentWidgetMessage[]): void {
    if (!this.#projectionFinalizationEnabled()) return;
    for (const message of messages) {
      const projection = divergentDisplayProjection(message);
      if (projection !== undefined) {
        this.#acknowledgedProjections.set(message.id, projection);
      }
    }
  }

  /**
   * After a terminal assistant message, finalize any browser-derived projection
   * the server cannot have. Painting is never delayed by this.
   */
  #scheduleProjectionFinalization(): void {
    if (!this.#projectionFinalizationEnabled()) return;
    const conversationId = this.#activeConversationId;
    if (!conversationId) return;
    const batch: HistoryDisplayProjection[] = [];
    for (const message of this.messages) {
      if (message.role !== "assistant" || message.streaming) continue;
      const projection = divergentDisplayProjection(message);
      if (projection === undefined) continue;
      if (this.#acknowledgedProjections.get(message.id) === projection) continue;
      batch.push({ id: message.id, displayContent: projection });
    }
    if (batch.length === 0) return;
    this.#setPendingProjections({
      conversationId,
      messageIds: batch.map((item) => item.id),
    });
    this.#enqueueProjectionFinalization(conversationId, batch);
  }

  /** Serialized: operations await this chain instead of racing it. */
  #enqueueProjectionFinalization(
    conversationId: string,
    batch: HistoryDisplayProjection[]
  ): void {
    const previous = this.#projectionFinalizationPromise ?? Promise.resolve();
    const chain = previous
      .catch(() => {})
      .then(() => this.#finalizeProjections(conversationId, batch, false))
      .finally(() => {
        if (this.#projectionFinalizationPromise === chain) {
          this.#projectionFinalizationPromise = null;
        }
      });
    this.#projectionFinalizationPromise = chain;
  }

  async #finalizeProjections(
    conversationId: string,
    batch: HistoryDisplayProjection[],
    retried: boolean
  ): Promise<void> {
    // A switch/reset/delete already invalidated this marker.
    if (this.#activeConversationId !== conversationId) return;
    // Two terminal scans can enqueue the same batch before the first PATCH
    // acknowledges; the chain serializes them, so re-filter at run time.
    const unacknowledged = batch.filter(
      (item) => this.#acknowledgedProjections.get(item.id) !== item.displayContent
    );
    if (unacknowledged.length === 0) {
      this.#clearFinalizedMarker(conversationId, batch);
      return;
    }
    try {
      const { conversationRevision } = await this.client.finalizeDisplayProjections(
        conversationId,
        unacknowledged
      );
      for (const item of unacknowledged) {
        this.#acknowledgedProjections.set(item.id, item.displayContent);
      }
      if (this.#activeConversationId === conversationId && conversationRevision) {
        this.#activeConversationRevision = conversationRevision;
      }
      this.#clearFinalizedMarker(conversationId, batch);
    } catch (error) {
      if (!retried && this.#isTransientProjectionError(error)) {
        await this.#finalizeProjections(conversationId, unacknowledged, true);
        return;
      }
      if (
        error instanceof HistoryClientError &&
        (error.code === "not_found" || error.code === "conversation_deleted")
      ) {
        // The record is gone: drop the projection instead of retrying forever.
        this.#clearFinalizedMarker(conversationId, batch);
        return;
      }
      // Marker retained for the next boot/dispatch; the turn itself succeeded.
      this.#notifyHistory(
        "projection_finalization_failed",
        "Some message formatting could not be saved for history; it will be retried."
      );
    }
  }

  /** Transport-shaped failures only; a contract failure is not retried. */
  #isTransientProjectionError(error: unknown): boolean {
    if (error instanceof HistoryClientError) return error.code === "request_failed";
    return error instanceof Error;
  }

  #clearFinalizedMarker(
    conversationId: string,
    batch: HistoryDisplayProjection[]
  ): void {
    const pending = this.#readPendingProjections();
    if (!pending || pending.conversationId !== conversationId) return;
    const done = new Set(batch.map((item) => item.id));
    const remaining = pending.messageIds.filter((id) => !done.has(id));
    this.#setPendingProjections(
      remaining.length ? { conversationId, messageIds: remaining } : null
    );
  }

  /**
   * Boot replay: rebuild the batch from the persisted ids plus the local
   * message content, then finalize before display reconciliation completes.
   */
  public async replayPendingProjections(): Promise<void> {
    if (!this.#projectionFinalizationEnabled()) return;
    const pending = this.#readPendingProjections();
    if (!pending) return;
    if (
      this.#activeConversationId &&
      pending.conversationId !== this.#activeConversationId
    ) {
      // Stale marker from another record: never replay it into this one.
      this.#setPendingProjections(null);
      return;
    }
    const batch: HistoryDisplayProjection[] = [];
    for (const id of pending.messageIds) {
      const message = this.messages.find((candidate) => candidate.id === id);
      if (!message || typeof message.content !== "string") continue;
      batch.push({ id, displayContent: message.content });
    }
    if (batch.length === 0) {
      this.#setPendingProjections(null);
      return;
    }
    this.#enqueueProjectionFinalization(pending.conversationId, batch);
    await this.#projectionFinalizationPromise?.catch(() => {});
  }

  /**
   * A local transcript mutation whose response carries no new revision: clear
   * the persisted one rather than guessing, so the next boot refreshes safely.
   */
  #invalidateConversationRevision(): void {
    if (!this.#activeConversationId) return;
    this.#activeConversationRevision = null;
    this.historyInternals.setStoredConversationRevision?.(null);
  }

  /** Blocks a send until the owning history transition finishes. */
  async #awaitHistorySendable(): Promise<void> {
    await this.awaitHistorySettled();
    await this.#completeCredentialReinit();
    if (this.#historyRecovery === "new_conversation_required") {
      await this.#retryRequiredConversation();
    }
  }

  static #isConversationDeleted(error: unknown): boolean {
    return (
      error instanceof HistoryClientError &&
      error.code === "conversation_deleted"
    );
  }

  /**
   * Safe 410 recovery (D5). The old payload would recreate the transcript that
   * was just deleted, so the retry carries ONLY the just-submitted user turn.
   */
  async #dispatchWithDeletedRecovery(
    snapshot: AgentWidgetMessage[],
    controller: AbortController,
    assistantMessageId: string,
    userMessageId: string,
    turnOptions?: {
      composerOptions?: ComposerOptionsPayload;
      interrupt?: boolean;
      voiceSpoken?: boolean;
      voicePriorToolResults?: VoicePriorToolResult[];
    }
  ): Promise<void> {
    const run = this.#activeRun;
    const emit = (event: AgentWidgetEvent) => {
      if (this.#activeRun === run) this.handleEvent(event);
    };
    this.#recordDispatchedProjections(snapshot);
    try {
      await this.client.dispatch(
        {
          messages: snapshot,
          signal: controller.signal,
          assistantMessageId,
          composerOptions: turnOptions?.composerOptions,
          interrupt: turnOptions?.interrupt,
          voiceSpoken: turnOptions?.voiceSpoken,
          voicePriorToolResults: turnOptions?.voicePriorToolResults
        },
        emit
      );
      return;
    } catch (error) {
      if (controller.signal.aborted || this.#activeRun !== run || !AgentWidgetSession.#isConversationDeleted(error)) throw error;
      const replacement = await this.#recoverFromDeletedConversation(userMessageId);
      // No recoverable turn (or no fresh record): surface the original 410.
      if (!replacement) throw error;
      // A second 410 propagates: exactly one recovery attempt per turn.
      this.#recordDispatchedProjections([replacement]);
      await this.client.dispatch(
        {
          messages: [replacement],
          signal: controller.signal,
          assistantMessageId,
          composerOptions: turnOptions?.composerOptions,
          voiceSpoken: turnOptions?.voiceSpoken,
          voicePriorToolResults: turnOptions?.voicePriorToolResults,
        },
        emit
      );
    }
  }

  /**
   * Capture only the newly submitted user message, destroy the deleted record's
   * local state, and initialize a fresh owned conversation to retry into.
   */
  async #recoverFromDeletedConversation(
    userMessageId: string
  ): Promise<AgentWidgetMessage | null> {
    const submitted = this.messages.find(
      (message) => message.id === userMessageId
    );
    if (!submitted) return null;
    const captured: AgentWidgetMessage = { ...submitted };

    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.#projectionFinalizationPromise = null;
    this.#discardConversationState(true);
    this.#activeConversationId = null;
    this.#activeConversationRevision = null;
    this.#historyNextMessageCursor = null;

    // The recovered turn is the whole transcript: no welcome bubble in front.
    this.#suppressWelcomeInjection = true;
    try {
      const provider = this.#historyProvider;
      if (provider) {
        const prepared = await provider.prepareStartNew({
          context: this.#historyContext(),
        });
        await this.#installFreshConversation(prepared, true);
      } else {
        this.client.clearClientSession();
        const session = await this.client.initSession();
        this.bindActivatedSession(session);
      }
    } catch {
      this.#historySendBlocked = true;
      this.#historyRecovery = "new_conversation_required";
      this.#emitHistoryState();
      this.#notifyHistory(
        "new_conversation_required",
        "That conversation was deleted. Start a new conversation to continue."
      );
      return null;
    } finally {
      this.#suppressWelcomeInjection = false;
    }

    // Restore only the user's own turn: it is the entire new transcript.
    this.#appendMessage(captured);
    this.#setStreaming(true);
    this.#notifyHistory(
      "conversation_deleted_recovered",
      "That conversation was deleted. Started a new conversation."
    );
    return this.messages.find((message) => message.id === captured.id) ?? captured;
  }

  public updateConfig(next: AgentWidgetConfig) {
    const previousArtifactDisplay = this.config.features?.artifacts?.display;
    const merged = { ...this.config, ...next };
    const replaceVoice = voiceConnectionChanged(this.config, merged);
    const replaceClient = connectionConfigChanged(this.config, merged);
    if (replaceClient) { this.cancel(); this.#clearBackgroundConversations(); }
    this.config = merged;
    if (replaceVoice) {
      this.cleanupVoice();
      if (merged.voiceRecognition?.enabled === true && usesSessionVoiceForConfig(merged)) {
        this.setupVoice();
      }
    }
    const artifactDisplayChanged =
      JSON.stringify(previousArtifactDisplay) !==
      JSON.stringify(merged.features?.artifacts?.display);

    // Connection/request-shaping change (apiUrl, clientToken, webmcp, headers,
    // parser, agent, …) → full client rebuild. UI-only change (theme, copy,
    // layout, suggestions, …) → refresh in place so the live stream, WebMCP
    // bridge, and any in-flight resolve survive. The latter is what makes a
    // self-styling widget work: a `webmcp:*` theme tool mutates config and
    // re-renders mid-turn; recreating the client there would abort the very
    // turn that's restyling the widget and strand the paused execution.
    if (!replaceClient) {
      this.client.updateConfig(merged);
      if (artifactDisplayChanged) this.#refreshArtifactReferenceBlocks();
      return;
    }

    // Replacing the client invalidates every in-flight WebMCP resolve, buffered
    // parallel-await batch, and pending approval bubble tied to the OLD client/
    // session. Tear them down BEFORE the swap (the new client has no session
    // yet) so a deferred batch flush or a parked confirm can't fire against the
    // fresh client: in client-token mode that would POST /resume without a
    // valid sessionId and strand the paused turn. Mirrors clearMessages' WebMCP
    // reset; the client swap already abandons any in-flight stream regardless.
    this.#abortWebMcpResolves();
    this.webMcpInflightKeys.clear();
    this.webMcpResolvedKeys.clear();
    const prevSSECallback = this.client.getSSEEventCallback();
    this.#historyOpenEpoch += 1;
    this.#historyContextEpoch += 1;
    this.client = new AgentWidgetClient(this.config, this.historyInternals);
    if (artifactDisplayChanged) this.#refreshArtifactReferenceBlocks();
    this.#wireDefaultWebMcpConfirm();
    if (prevSSECallback) {
      this.client.setSSEEventCallback(prevSSECallback);
    }
  }

  public getMessages() {
    return [...this.messages];
  }

  public getStatus() {
    return this.status;
  }

  public getRunActivity() { return this.#runActivity; }

  public isStreaming() {
    return this.streaming;
  }

  /**
   * @deprecated Use injectMessage() instead.
   * Injects a raw event into the session event handler.
   */
  public injectTestEvent(event: AgentWidgetEvent) {
    this.handleEvent(event);
  }

  /**
   * Inject a message into the conversation.
   * This is the primary API for adding messages programmatically.
   *
   * Supports dual-content where the displayed content differs from what the LLM receives.
   *
   * @param options - Message injection options including dual-content support
   * @returns The created message object
   *
   * @example
   * // Same content for user and LLM
   * session.injectMessage({
   *   role: 'assistant',
   *   content: 'Here are the search results...'
   * });
   *
   * @example
   * // Different content for user and LLM (redaction)
   * session.injectMessage({
   *   role: 'assistant',
   *   content: '**Found 3 products:**\n- iPhone 15 Pro ($1,199)\n- iPhone 15 ($999)',
   *   llmContent: '[Search results: 3 iPhone products, $799-$1199]'
   * });
   */
  public injectMessage(options: InjectMessageOptions): AgentWidgetMessage {
    const message = this.#createInjectedMessage(options);
    // Use upsert to handle both new messages and updates (streaming).
    this.upsertMessage(message);
    return message;
  }

  /** Shared by single and batch injection, including dispatch error detail. */
  #createInjectedMessage(options: InjectMessageOptions): AgentWidgetMessage {
    const {
      role,
      content,
      llmContent,
      contentParts,
      id,
      createdAt,
      sequence,
      streaming = false,
      voiceProcessing,
      voiceCaption,
      rawContent
    } = options;

    // Generate appropriate ID based on role
    const messageId =
      id ??
      (role === "user"
        ? generateUserMessageId()
        : role === "assistant"
          ? generateAssistantMessageId()
          : `${role === "operator" ? "operator" : "system"}-${Date.now()}-${Math.random().toString(16).slice(2)}`);

    return {
      id: messageId,
      role,
      content,
      createdAt: createdAt ?? new Date().toISOString(),
      sequence: sequence ?? this.#nextSequence(),
      streaming,
      // Only include optional fields if provided
      ...(llmContent !== undefined && { llmContent }),
      ...(contentParts !== undefined && { contentParts }),
      ...(voiceProcessing !== undefined && { voiceProcessing }),
      ...(voiceCaption && { voiceCaption }),
      ...(rawContent !== undefined && { rawContent }),
      ...(options.dispatchError !== undefined && { dispatchError: options.dispatchError }),
      ...(options.author !== undefined && { author: { ...options.author } })
    };

  }

  /**
   * Convenience method for injecting assistant messages.
   * Role defaults to 'assistant'.
   *
   * @example
   * // Simple assistant message
   * session.injectAssistantMessage({
   *   content: 'Here are your search results...'
   * });
   *
   * @example
   * // With redacted LLM content
   * session.injectAssistantMessage({
   *   content: 'Full product details for the user...',
   *   llmContent: '[Product details summary]'
   * });
   */
  public injectAssistantMessage(
    options: InjectAssistantMessageOptions
  ): AgentWidgetMessage {
    return this.injectMessage({ ...options, role: "assistant" });
  }

  /**
   * Convenience method for injecting user messages.
   * Role defaults to 'user'.
   *
   * @example
   * session.injectUserMessage({
   *   content: 'Add iPhone 15 Pro to my cart'
   * });
   */
  public injectUserMessage(
    options: InjectUserMessageOptions
  ): AgentWidgetMessage {
    return this.injectMessage({ ...options, role: "user" });
  }

  /**
   * Convenience method for injecting system messages.
   * Role defaults to 'system'.
   *
   * @example
   * // Inject context that guides LLM behavior
   * session.injectSystemMessage({
   *   content: '[Context updated]',  // Minimal display
   *   llmContent: 'User is viewing iPhone 15 Pro. Cart has 2 items.'
   * });
   */
  public injectSystemMessage(
    options: InjectSystemMessageOptions
  ): AgentWidgetMessage {
    return this.injectMessage({ ...options, role: "system" });
  }

  /**
   * Inject multiple messages in a single batch with one sort and one render pass.
   */
  public injectMessageBatch(optionsList: InjectMessageOptions[]): AgentWidgetMessage[] {
    const results = optionsList.map((options) => this.#createInjectedMessage(options));

    // Add all messages, sort once, notify once
    this.messages = this.#sortMessages([...this.messages, ...results]);
    this.callbacks.onMessagesChanged([...this.messages]);

    return results;
  }

  /**
   * Convenience method for injecting a registered component directive as
   * an assistant message: the same shape Persona produces from a streamed
   * `{ "text": "...", "component": "...", "props": {...} }` payload.
   *
   * Sets `content` to `text`, `rawContent` to the JSON directive (so
   * `extractComponentDirectiveFromMessage` can find it), and forwards
   * `llmContent` / `id` / `createdAt` / `sequence`.
   *
   * @example
   * session.injectComponentDirective({
   *   component: "DynamicForm",
   *   props: { title: "Book a demo", fields: [...] },
   *   text: "Share your details to book a demo.",
   *   llmContent: "[Showed booking form]"
   * });
   */
  public injectComponentDirective(
    options: InjectComponentDirectiveOptions
  ): AgentWidgetMessage {
    const {
      component,
      props = {},
      text = "",
      llmContent,
      id,
      createdAt,
      sequence
    } = options;

    const directive: { text: string; component: string; props: Record<string, unknown> } = {
      text,
      component,
      props
    };

    return this.injectMessage({
      role: "assistant",
      content: text,
      rawContent: JSON.stringify(directive),
      ...(llmContent !== undefined && { llmContent }),
      ...(id !== undefined && { id }),
      ...(createdAt !== undefined && { createdAt }),
      ...(sequence !== undefined && { sequence })
    });
  }

  /** Replace props without changing the component message's identity or ordering. */
  public updateComponentProps(messageId: string, props: Record<string, unknown>): boolean {
    const message = this.messages.find((candidate) => candidate.id === messageId);
    const directive = message && extractComponentDirectiveFromMessage(message);
    if (!message || message.role !== "assistant" || message.variant || !directive) return false;
    this.upsertMessage({
      ...message,
      rawContent: JSON.stringify({ text: message.content, component: directive.component, props }),
    });
    return true;
  }

  /** Remove one component directive, leaving the surrounding conversation intact. */
  public removeComponentMessage(messageId: string): boolean {
    const message = this.messages.find((candidate) => candidate.id === messageId);
    if (!message || message.role !== "assistant" || message.variant || !extractComponentDirectiveFromMessage(message)) return false;
    this.messages = this.messages.filter((candidate) => candidate.id !== messageId);
    this.callbacks.onMessagesChanged([...this.messages]);
    return true;
  }

  /**
   * Resolve the gathered mentions and merge them into the user message's
   * model-visible content. Default path is `llmAppend` → `llmContent` (or a text
   * `contentPart` when parts are present, since `contentParts` wins the priority
   * chain); the opt-in structured `context` lands on `mentionContext` for the
   * client to namespace into the request `context`. Failures were already
   * dropped by `finalize()`.
   */
  async #applyMentionBundle(
    userMessage: AgentWidgetMessage,
    typedText: string,
    finalize: () => Promise<MentionSubmitBundle>
  ): Promise<void> {
    let bundle: MentionSubmitBundle;
    try {
      bundle = await finalize();
    } catch {
      return; // resolution failed entirely; send the typed text as-is
    }

    // Blocks arrive pre-formatted from the mention layer (fenced/document/custom
    // per `contextMentions.llmFormat`); the session just joins them and appends
    // the typed prose last.
    const block = bundle.blocks.join("\n\n");
    const llmText = [block, typedText].filter(Boolean).join("\n\n");

    const hasAttachments =
      Array.isArray(userMessage.contentParts) &&
      userMessage.contentParts.length > 0;
    const hasMentionParts = bundle.contentParts.length > 0;

    if (hasAttachments) {
      // The typed text already rides as a content part; add the block + mention
      // parts ahead of the existing attachment parts (mentions first).
      const parts: ContentPart[] = [];
      if (block) parts.push(createTextPart(block));
      parts.push(...bundle.contentParts);
      parts.push(...userMessage.contentParts!);
      userMessage.contentParts = parts;
    } else if (hasMentionParts) {
      const parts: ContentPart[] = [];
      if (llmText) parts.push(createTextPart(llmText));
      parts.push(...bundle.contentParts);
      userMessage.contentParts = parts;
    } else if (block) {
      userMessage.llmContent = llmText;
    }

    if (Object.keys(bundle.context).length > 0) {
      userMessage.mentionContext = bundle.context;
    }
  }

  public async sendMessage(
    rawInput: string,
    options?: {
      viaVoice?: boolean;
      /** Multi-modal content parts (e.g., images) to include with the message */
      contentParts?: ContentPart[];
      /**
       * Context mentions gathered from the composer. `refs` echo immediately as
       * chips; `finalize()` resolves any pending/submit sources just before
       * dispatch (after the instant echo) and the bundle is merged into this
       * message's model-visible content.
       */
      mentions?: {
        refs: AgentWidgetContextMentionRef[];
        finalize: () => Promise<MentionSubmitBundle>;
      };
      /**
       * Ordered prose + mention segments for inline-mode display (`display:
       * "inline"`). Stored on the user message so its bubble re-renders `@tokens`
       * in place (and suppresses the chip row). Display/transcript only — the
       * model still sees resolved bodies via the `mentions` bundle above.
       */
      contentSegments?: AgentWidgetContentSegment[];
      /**
       * Per-turn composer selections (model, active modes) captured in the
       * submission snapshot. Rides the request as `composerOptions`; each
       * transport decides what it means (see the type's JSDoc).
       */
      composerOptions?: ComposerOptionsPayload;
      /**
       * Quote/reply-to for this turn. The delimited block goes ahead of the
       * user's text on the model channel; the structured source is retained on
       * the message. Without a quote nothing about the message changes.
       */
      quote?: ComposerQuote;
      /**
       * Internal (`resubmitFrom` retry only): fields copied verbatim onto the
       * rebuilt user message so an already-resolved turn replays without
       * re-running mention finalizers.
       */
      replayFields?: Pick<
        AgentWidgetMessage,
        | "llmContent"
        | "rawContent"
        | "mentionContext"
        | "contextMentions"
        | "quote"
      >;
      /**
       * Client-token only: this turn supersedes the in-flight one
       * (`composer.streamingSubmitBehavior: "interrupt"`).
       */
      interrupt?: boolean;
      /**
       * Internal (voice client delegation): this is the delegated voice turn.
       * `userMessageId` names an existing transcript bubble to submit as the
       * turn's user message instead of appending a new one, so the spoken
       * request appears (and is sent) exactly once. `priorToolResults`: page
       * tools the voice server already ran for this turn, recorded once as
       * completed tool rows after the request (see voicePriorToolRows).
       */
      voiceTurn?: { userMessageId?: string | null; priorToolResults?: unknown };
    }
  ) {
    const input = rawInput.trim();
    // Allow sending if there's text OR attachments OR mentions. A retry replay
    // also qualifies on its already-resolved model channel alone (a mention-only
    // turn has empty display text).
    const hasReplayContent =
      !!options?.replayFields &&
      (options.replayFields.llmContent !== undefined ||
        options.replayFields.rawContent !== undefined ||
        (options.replayFields.contextMentions?.length ?? 0) > 0);
    if (
      !input &&
      (!options?.contentParts || options.contentParts.length === 0) &&
      (!options?.mentions || options.mentions.refs.length === 0) &&
      !hasReplayContent
    )
      return;

    // Any other send replaces a delegated voice turn still in flight: that
    // turn fails, and this one's reply is neither its answer nor voice-spoken.
    if (this.#voiceDelegation && !options?.voiceTurn) {
      this.#voiceDelegation.failed = true;
      this.#voiceDelegation = null;
    }

    // History transitions (continuity wipe, boot reconciliation, projection
    // finalization, replacement init, external credential change) own the
    // record this turn would land in.
    if (
      this.#historyGate ||
      this.#projectionFinalizationPromise ||
      this.#historySendBlocked ||
      this.#credentialReinitPending
    ) {
      await this.#awaitHistorySendable();
    }

    this.stopSpeaking();
    this.client.cancelStreams();
    this.abortController?.abort();
    this.#beginRun();
    // A new user turn supersedes any in-flight WebMCP resolve from the prior
    // turn. Tear them down here (they own controllers separate from the shared
    // one) so a lingering resolve can't race the new dispatch or post a stale
    // /resume against a superseded execution.
    this.#abortWebMcpResolves();
    this.#actionsEpoch++;
    // A new turn also supersedes any pending durable reconnect from the prior
    // turn (cancels backoff/listeners, clears the old resume handle).
    this.#teardownReconnect();

    const voiceBubbleId = options?.voiceTurn?.userMessageId;
    const voiceBubble = voiceBubbleId
      ? this.messages.find((m) => m.id === voiceBubbleId && m.role === "user")
      : undefined;
    // Generate IDs for both user message and expected assistant response
    const userMessageId = voiceBubble?.id ?? generateUserMessageId();
    const assistantMessageId = generateAssistantMessageId();
    // The active assistant bubble for a durable reconnect is captured from the
    // real streamed message events (see handleEvent), not pre-assigned here:
    // the proxy path auto-generates a different id than `assistantMessageId`.
    this.#activeAssistantMessageId = null;

    // Fallback display text ONLY when the sole content is image attachments.
    // A mention/command-only submit (empty text + a chip) must NOT read as
    // "[Image]"; it renders its chips with empty text instead.
    const imageOnlyFallback =
      options?.contentParts && hasImages(options.contentParts)
        ? IMAGE_ONLY_MESSAGE_FALLBACK_TEXT
        : "";

    const userMessage: AgentWidgetMessage = {
      id: userMessageId,
      role: "user",
      content: input || imageOnlyFallback, // Display text (fallback if only images)
      createdAt: new Date().toISOString(),
      sequence: this.#nextSequence(),
      viaVoice: options?.viaVoice || false,
      // Include contentParts if provided (for multi-modal messages)
      ...(options?.contentParts && options.contentParts.length > 0 && {
        contentParts: options.contentParts
      }),
      // Echo mention chips immediately (refs only; payloads merged below).
      ...(options?.mentions && options.mentions.refs.length > 0 && {
        contextMentions: options.mentions.refs
      }),
      // Inline mode: ordered display segments for in-prose `@token` rendering.
      ...(options?.contentSegments && options.contentSegments.length > 0 && {
        contentSegments: options.contentSegments
      }),
      // Retained for retry fidelity; never re-read for the current dispatch.
      ...(options?.composerOptions && { composerOptions: options.composerOptions }),
      // A retry replays the already-resolved model channel verbatim.
      ...(options?.replayFields ?? {})
    };

    if (voiceBubble) {
      // The transcript bubble is the user message: the model gets the request
      // text the voice model delegated, the bubble keeps what was heard.
      voiceBubble.viaVoice = true;
      delete voiceBubble.voiceCaption;
      if (voiceBubble.content.trim() !== input) voiceBubble.llmContent = input;
      this.callbacks.onMessagesChanged([...this.messages]);
    } else {
      this.#appendMessage(userMessage);
    }
    this.#setStreaming(true);

    // Assign the fresh controller BEFORE the mention await so cancel() (or a
    // superseding sendMessage) during finalize() aborts THIS turn, not a stale
    // prior controller.
    const controller = new AbortController();
    this.abortController = controller;
    const run = this.#activeRun;

    // Resolve + merge mentions AFTER the instant echo but BEFORE dispatch, so
    // the model sees the context while the user's bubble already rendered.
    // `appendMessage` stores a sequence-normalized COPY (see `ensureSequence`),
    // so mutate THAT live reference — not the orphaned `userMessage` literal —
    // or the merged `llmContent`/`contentParts`/`mentionContext` never reach the
    // dispatch snapshot below. Re-emit so any merged parts also reach the UI.
    if (options?.mentions) {
      const stored =
        this.messages.find((m) => m.id === userMessageId) ?? userMessage;
      await this.#applyMentionBundle(stored, input, options.mentions.finalize);
      // A cancel() or new sendMessage during finalize aborted this controller
      // (and replaced/nulled the shared ref). Bail without dispatching and
      // leave whatever idle/streaming state that caller already set.
      if (controller.signal.aborted || this.abortController !== controller) {
        return;
      }
      this.callbacks.onMessagesChanged([...this.messages]);
    }

    // Quote last, so its delimited block is the outermost prefix on the model
    // channel (ahead of any mention blocks and the user's prose). Same stored-ref
    // rule as the mention merge above. A retry already carries resolved content,
    // so `replayFields` skips this entirely.
    if (options?.quote && !options.replayFields) {
      const stored =
        this.messages.find((m) => m.id === userMessageId) ?? userMessage;
      const quoted = applyQuoteToContent({
        quote: options.quote,
        text: stored.llmContent ?? stored.content,
        contentParts: stored.contentParts
      });
      if (quoted.contentParts) stored.contentParts = quoted.contentParts;
      if (quoted.llmContent !== undefined) stored.llmContent = quoted.llmContent;
      if (quoted.contentParts || quoted.llmContent !== undefined) {
        stored.quote = { ...options.quote };
        this.callbacks.onMessagesChanged([...this.messages]);
      }
    }

    // A submitted voice bubble may sit above captions or a turn that finished
    // while it waited: the request still ends on it.
    const sentAt = new Date().toISOString();
    const snapshot = voiceBubble
      ? [...this.messages.filter((m) => m !== voiceBubble), { ...voiceBubble, createdAt: sentAt }]
      : [...this.messages];
    // Tools the voice server already ran answer this request and are never run
    // again. Stored at the request's own time, so every later replay keeps them
    // right after it; this request (moved to `sentAt`) sends them at `sentAt`.
    const requestAt = (voiceBubble ?? this.messages.find((m) => m.id === userMessageId))?.createdAt ?? sentAt;
    const priorRows = voicePriorToolRows(options?.voiceTurn?.priorToolResults, requestAt);
    for (const row of priorRows) {
      if (this.messages.some((m) => m.id === row.id)) continue;
      this.#appendMessage(row);
      snapshot.push({ ...row, createdAt: sentAt });
    }
    // Client-token chat replays from the server's transcript: it gets them as a field.
    const voicePriorToolResults = priorRows.map(({ agentMetadata }) => {
      const { toolCallId, toolName, args, result } = agentMetadata!.clientToolAnswer!;
      return { toolCallId, name: toolName, arguments: args as Record<string, unknown>, result };
    });

    try {
      await this.#dispatchWithDeletedRecovery(
        snapshot,
        controller,
        assistantMessageId,
        userMessageId,
        {
          composerOptions: options?.composerOptions,
          interrupt: options?.interrupt,
          // The delegated voice turn's reply is read aloud (spoken-reply hint).
          voiceSpoken: !!options?.voiceTurn,
          ...(voicePriorToolResults.length > 0 && { voicePriorToolResults })
        }
      );
    } catch (error) {
      if (this.#activeRun !== run) return;
      // A durable drop fired the dispatch wrapper's `finally` (plain `idle`)
      // first, which already flipped us into `resuming` and armed reconnect.
      // The subsequent dispatch rejection must NOT paint a dispatch-error
      // bubble: the turn is being resumed, not failed.
      if (this.status === "resuming" || this.#reconnecting) return;
      if (this.#voiceDelegation) this.#voiceDelegation.failed = true;
      // Check if this is an abort error (user canceled, navigated away, etc.)
      // In these cases, don't show fallback - the request was intentionally interrupted
      const isAbortError =
        error instanceof Error &&
        (error.name === 'AbortError' ||
         error.message.includes('aborted') ||
         error.message.includes('abort'));

      if (!isAbortError) {
        this.#appendDispatchError(error);
      }
      this.#setStatus("idle");
      this.#setStreaming(false);
      this.abortController = null;
      this.#finishRun(isAbortError ? "cancel" : "error", run);

      if (!isAbortError) {
        if (error instanceof Error) {
          this.callbacks.onError?.(error);
        } else {
          this.callbacks.onError?.(new Error(String(error)));
        }
      }
    }
  }

  /**
   * Re-run the conversation from one user turn (docs/composer-gap-analysis.md
   * section 10). Backs both retry/regenerate and edit-and-resend.
   *
   * The turn is the user message plus every message the assistant produced for
   * it (text, reasoning, tool, approval, system variants) up to the next user
   * message. v1 replaces the active tail destructively: the turn AND everything
   * after it are dropped, so no branch is retained and the UI must not imply
   * one. Persistence follows one emit: the truncation is silent and the first
   * `onMessagesChanged` a host sees already carries the new attempt, so storage
   * never holds a transcript with the turn removed and nothing in its place.
   *
   * Without `replacement` the STORED user message is replayed field for field
   * (`contentParts`, `llmContent`, `rawContent`, mention refs and context,
   * inline segments, quote, composer options, voice flag). With one, the
   * replacement snapshot is sent through the normal submission path. Either way
   * the new attempt gets fresh message ids.
   *
   * Returns false when the id is unknown, is not a user message, or the rebuilt
   * turn would carry no content, or truncation would remove an operator reply;
   * nothing is truncated in those cases.
   */
  public resubmitFrom(
    messageId: string,
    options: ComposerResubmitOptions
  ): boolean {
    const boundary = findUserTurnBoundary(this.messages, messageId);
    if (!boundary || !canResubmitUserMessage(this.messages, messageId)) return false;

    // Operate on the STORED message, never a caller-held object: appendMessage
    // keeps a copy (see `ensureSequence`), so the array entry is the only ref
    // that carries the merged mention/quote content.
    const stored = this.messages[boundary.start];
    const replacement = options.replacement;

    const text = replacement ? replacement.text : stored.content;
    const contentParts = replacement
      ? replacement.contentParts
      : stored.contentParts;
    const hasContent =
      !!text?.trim() ||
      (contentParts?.length ?? 0) > 0 ||
      (!replacement && (stored.llmContent?.trim()?.length ?? 0) > 0);
    if (!hasContent) return false;

    const replay = replacement
      ? undefined
      : {
          ...(stored.llmContent !== undefined && { llmContent: stored.llmContent }),
          ...(stored.rawContent !== undefined && { rawContent: stored.rawContent }),
          ...(stored.mentionContext && { mentionContext: stored.mentionContext }),
          ...(stored.contextMentions && { contextMentions: stored.contextMentions }),
          ...(stored.quote && { quote: stored.quote })
        };

    const composerOptions = replacement
      ? this.#toComposerOptionsPayload(replacement.options)
      : stored.composerOptions;
    const contentSegments = replacement
      ? replacement.contentSegments
      : stored.contentSegments;
    const quote = replacement ? replacement.options?.quote : undefined;
    const viaVoice = replacement ? replacement.viaVoice : stored.viaVoice;

    // Cancel before history changes so no in-flight stream can write into the
    // tail we are about to drop.
    this.cancel();

    // Silent truncation: the emit belongs to the resubmission below.
    this.messages = this.messages.slice(0, boundary.start);

    void this.sendMessage(text ?? "", {
      ...(contentParts?.length ? { contentParts: [...contentParts] } : {}),
      ...(contentSegments?.length ? { contentSegments: [...contentSegments] } : {}),
      ...(composerOptions ? { composerOptions } : {}),
      ...(quote ? { quote } : {}),
      ...(replay && Object.keys(replay).length > 0 ? { replayFields: replay } : {}),
      viaVoice: viaVoice === true
    });
    return true;
  }

  /** Submission options → the wire payload shape; quote travels separately. */
  #toComposerOptionsPayload(
    options: { selectedModelId?: string; activeModeIds?: string[] } | undefined
  ): ComposerOptionsPayload | undefined {
    if (!options) return undefined;
    const payload: ComposerOptionsPayload = {};
    if (options.selectedModelId) payload.selectedModelId = options.selectedModelId;
    if (options.activeModeIds?.length) {
      payload.activeModeIds = [...options.activeModeIds];
    }
    return Object.keys(payload).length > 0 ? payload : undefined;
  }

  /**
   * Continue the conversation without adding a new user message.
   * Triggers the model to respond based on the current conversation state.
   *
   * Use this for automatic continuation after action handlers inject data
   * (e.g., search results) that the model should analyze.
   *
   * @example
   * // After injecting search results, trigger model to analyze them
   * session.injectAssistantMessage({ content: 'Found 5 products...' });
   * session.continueConversation();
   */
  public async continueConversation() {
    // Don't continue if already streaming
    if (this.streaming) return;

    // Same ownership rule as sendMessage: a pending history transition owns the
    // record this turn would land in.
    if (
      this.#historyGate ||
      this.#projectionFinalizationPromise ||
      this.#historySendBlocked ||
      this.#credentialReinitPending
    ) {
      await this.#awaitHistorySendable();
    }

    this.abortController?.abort();
    this.#teardownReconnect();
    this.#beginRun();

    const assistantMessageId = generateAssistantMessageId();
    this.#activeAssistantMessageId = null;

    this.#setStreaming(true);

    const controller = new AbortController();
    this.abortController = controller;
    const run = this.#activeRun;

    const snapshot = [...this.messages];

    try {
      await this.client.dispatch(
        {
          messages: snapshot,
          signal: controller.signal,
          assistantMessageId
        },
        (event) => {
          if (this.#activeRun === run) this.handleEvent(event);
        }
      );
    } catch (error) {
      if (this.#activeRun !== run) return;
      if (this.status === "resuming" || this.#reconnecting) return;
      // Check if this is an abort error (a prior in-flight stream was canceled,
      // the user navigated away, etc.). In these cases, don't show fallback or
      // fire onError - the request was intentionally interrupted.
      const isAbortError =
        error instanceof Error &&
        (error.name === 'AbortError' ||
         error.message.includes('aborted') ||
         error.message.includes('abort'));

      if (!isAbortError) {
        this.#appendDispatchError(error);
      }
      this.#finishRun(isAbortError ? "cancel" : "error", run);
      this.#setStatus("idle");
      this.#setStreaming(false);
      this.abortController = null;
      if (!isAbortError) {
        if (error instanceof Error) {
          this.callbacks.onError?.(error);
        } else {
          this.callbacks.onError?.(new Error(String(error)));
        }
      }
    }
  }

  /**
   * Connect an external SSE stream (e.g. from an approval endpoint) and
   * process it through the SDK's native event pipeline.
   */
  public async connectStream(
    stream: ReadableStream<Uint8Array>,
    options?: {
      assistantMessageId?: string;
      allowReentry?: boolean;
      /**
       * Durable reconnect: keep the assistant message identified by
       * `assistantMessageId` OPEN so the replayed deltas keep filling it. The
       * default stale-finalize pass would otherwise seal the very bubble we're
       * resuming into.
       */
      preserveAssistantId?: boolean;
      /**
       * Durable reconnect: text already shown in the resumed bubble, so the
       * fresh stream's accumulator continues from it (the replay carries only
       * post-cursor deltas, not the full text).
       */
      seedContent?: string;
      /** Never execute browser tools from durable background event replay. */
      readOnlyReplay?: boolean;
    }
  ): Promise<void> {
    if (this.streaming && !options?.allowReentry) return;
    if (!this.#activeRun) this.#beginRun();
    if (!options?.allowReentry) {
      this.abortController?.abort();
    }

    // Durable reconnect keeps filling the same bubble: track its id so the
    // cursor handler attaches to it.
    if (options?.preserveAssistantId && options.assistantMessageId) {
      this.#activeAssistantMessageId = options.assistantMessageId;
    }

    // Finalize any stale streaming messages from the previous stream
    // (e.g., tool messages interrupted by approval pause), except the bubble
    // an active reconnect is resuming into.
    const preserveId = options?.preserveAssistantId
      ? options.assistantMessageId
      : undefined;
    let hasStale = false;
    for (const msg of this.messages) {
      if (msg.streaming && msg.id !== preserveId) {
        msg.streaming = false;
        hasStale = true;
      }
    }
    if (hasStale) {
      this.callbacks.onMessagesChanged([...this.messages]);
    }

    this.#setStreaming(true);
    const run = this.#activeRun;

    const streamToken = {};
    try {
      await this.client.processStream(
        stream,
        (event) => {
          if (this.#activeRun !== run) return;
          const previous = this.#activeStreamToken;
          this.#activeStreamToken = streamToken;
          try {
            this.handleEvent(event, options?.readOnlyReplay);
          } finally {
            this.#activeStreamToken = previous;
          }
        },
        options?.assistantMessageId,
        options?.seedContent,
        this.abortController?.signal,
        options?.readOnlyReplay ? this.messages : undefined,
        options?.readOnlyReplay ? this.#streamCheckpoint : undefined
      );
      this.#flushApprovalSettles(streamToken);
    } catch (error) {
      if (this.#activeRun !== run) return;
      // During a durable reconnect a thrown resume stream is just another drop:
      // the reconnect loop owns the retry/backoff. Don't paint an error or tear
      // down, stay in `resuming`. The reconnect may still deliver the paused
      // call's result, so its approval settles wait for streaming to end.
      if (this.status === "resuming" || this.#reconnecting) {
        this.#releaseApprovalSettles(streamToken);
        return;
      }
      this.#flushApprovalSettles(streamToken);
      this.#setStatus("error");
      // Mirror the idle/error handlers: a failed resume stream must not tear
      // down streaming/abortController while another WebMCP resolve is still
      // confirming or executing. The in-flight resolve's `finally` owns the
      // teardown once `webMcpResolveControllers` drains.
      if (this.webMcpResolveControllers.size === 0) {
        this.#setStreaming(false);
        this.abortController = null;
      }
      this.callbacks.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Install the native approval-bubble confirm handler on the WebMCP bridge
   * when the integrator hasn't supplied a custom `webmcp.onConfirm`. Without
   * this, the bridge falls back to a blunt `window.confirm`. Safe to call
   * repeatedly (e.g. after the client is re-created in `updateConfig`).
   */
  #wireDefaultWebMcpConfirm(): void {
    const webmcp = this.config.webmcp;
    if (webmcp?.enabled === true && !webmcp.onConfirm) {
      this.client.setWebMcpConfirmHandler((info) =>
        this.requestWebMcpApproval(info)
      );
    }
  }

  /**
   * Default WebMCP confirm gate: render Persona's native in-panel approval
   * bubble and resolve when the user clicks Approve/Deny. Returns immediately
   * with `true` when `webmcp.autoApprove(info)` opts the tool out of the gate
   * (e.g. a read-only catalog search), so no bubble is shown. The bridge
   * awaits this Promise before executing the page tool.
   */
  public requestWebMcpApproval(info: WebMcpConfirmInfo): Promise<boolean> {
    // Per-tool policy hook: auto-allow opted-out tools without any UI. A
    // throwing predicate must not block the call, so fall through to an
    // explicit gate on error.
    try {
      if (this.config.webmcp?.autoApprove?.(info) === true) {
        return Promise.resolve(true);
      }
    } catch {
      // fall through to explicit approval
    }
    if (info.signal?.aborted) return Promise.resolve(false);

    let approvalId: string;
    do {
      approvalId = `webmcp-${++this.#webMcpApprovalSeq}`;
    } while (this.messages.some(message =>
      message.id === `approval-${approvalId}` || message.approval?.id === approvalId
    ));
    const approval: AgentWidgetApproval = {
      id: approvalId,
      status: "pending",
      agentId: "",
      executionId: "",
      toolName: info.toolName,
      ...(info.toolCallId ? { toolCallId: info.toolCallId } : {}),
      toolType: "webmcp",
      description:
        info.description ?? `Allow the assistant to run ${info.toolName}?`,
      parameters: info.args,
    };
    const approvalMessageId = `approval-${approval.id}`;

    this.upsertMessage({
      id: approvalMessageId,
      role: "assistant",
      content: "",
      createdAt: new Date().toISOString(),
      streaming: false,
      variant: "approval",
      approval,
    });

    return new Promise<boolean>((resolve) => {
      this.webMcpApprovalResolvers.set(approvalMessageId, resolve);
      // The call gave up (deadline or cancel): close the card so a late click cannot look like it ran the tool.
      info.signal?.addEventListener(
        "abort",
        () => this.resolveWebMcpApproval(approvalMessageId, "timeout"),
        { once: true }
      );
    });
  }

  /**
   * Resolve a pending WebMCP approval bubble (from the Approve/Deny click in
   * `ui.ts`). Updates the bubble to its resolved state and unblocks the
   * bridge Promise parked in `requestWebMcpApproval`. No-op if already
   * resolved (double-click, re-render).
   */
  public resolveWebMcpApproval(
    approvalMessageId: string,
    decision: "approved" | "denied" | "timeout"
  ): void {
    const resolve = this.webMcpApprovalResolvers.get(approvalMessageId);
    if (!resolve) return;
    this.webMcpApprovalResolvers.delete(approvalMessageId);

    const existing = this.messages.find((m) => m.id === approvalMessageId);
    if (existing?.approval) {
      this.upsertMessage({
        ...existing,
        approval: {
          ...existing.approval,
          status: decision,
          resolvedAt: Date.now(),
        },
      });
    }

    resolve(decision === "approved");
  }

  #actionsHost: SessionActionsHost | null = null;
  // Advanced when the turn is stopped or replaced (cancel, clear, new send,
  // hydrate, conversation switch). A resolve waiting on the lazy chunk checks
  // it after the load so a stopped turn never resumes late.
  #actionsEpoch = 0;

  /**
   * Run a resolve path from `session-actions.ts` (the lazy
   * `session-actions.js` chunk in the IIFE build). Synchronous when the module
   * is already loaded or provided, so the call's timing matches an inline
   * method; otherwise it waits for the chunk and reports a failed load via
   * `onError` (the resolve never started, so no state needs unwinding). A turn
   * stopped or replaced while the chunk loads drops the resolve.
   */
  #withActions(
    run: (actions: SessionActionsModule, host: SessionActionsHost) => Promise<void>
  ): Promise<void> {
    const host = (this.#actionsHost ??= {
      s: this as unknown as SessionActionsInternals,
      setStreaming: (streaming) => this.#setStreaming(streaming),
      appendMessage: (message) => this.#appendMessage(message),
      nextSequence: () => this.#nextSequence(),
      settleApprovalPausedToolCall: (id) => this.#settleApprovalPausedToolCall(id),
      resumeAfter: (executionId) =>
        this.#resumable?.executionId === executionId
          ? this.#resumable.lastEventId
          : undefined,
    });
    const actions = getSessionActionsSync();
    if (actions) return run(actions, host);
    const epoch = this.#actionsEpoch;
    return loadSessionActions().then(
      (loaded) => (epoch === this.#actionsEpoch ? run(loaded, host) : undefined),
      (error) => {
        this.callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    );
  }

  /**
   * Resolve a tool approval request (approve or deny).
   * Updates the approval message status, calls the API (or custom onDecision),
   * and pipes the response stream through connectStream().
   */
  public resolveApproval(
    approval: AgentWidgetApproval,
    decision: 'approved' | 'denied',
    options?: AgentWidgetApprovalDecisionOptions
  ): Promise<void> {
    return this.#withActions((actions, host) =>
      actions.resolveApproval(host, approval, decision, options)
    );
  }

  /**
   * Resolve a paused `ask_user_question` LOCAL tool call.
   *
   * When the server emits `await` for `ask_user_question`, the widget
   * renders the answer-pill sheet and calls this method once the user
   * picks. Steps:
   *   1. POST the answer to `/resume` via `client.resumeFlow`.
   *   2. Pipe the resulting SSE stream through `connectStream()` so the
   *      paused agent execution continues.
   *   3. Append a user-visible bubble with the answer text so the
   *      transcript reads naturally.
   */
  public resolveAskUserQuestion(
    toolMessage: AgentWidgetMessage,
    answer: string | Record<string, string | string[]>
  ): Promise<void> {
    return this.#withActions((actions, host) =>
      actions.resolveAskUserQuestion(host, toolMessage, answer)
    );
  }

  /**
   * Resolve a paused auto-resolving LOCAL tool call (`webmcp:*` page tools
   * and the built-in `suggest_replies`) and post the result to `/resume`.
   * Triggered automatically from `handleEvent`; idempotent on the message's
   * `toolCall.id`. See `resolveWebMcpToolCall` in `session-actions.ts`.
   */
  public resolveWebMcpToolCall(toolMessage: AgentWidgetMessage): Promise<void> {
    return this.#withActions((actions, host) =>
      actions.resolveWebMcpToolCall(host, toolMessage)
    );
  }

  /**
   * Persist in-progress answers and the current page index for a multi-question
   * `ask_user_question` payload, so a refresh resumes on the same page with
   * prior answers intact. Called by ui.ts on every Back/Next/pick interaction.
   */
  public persistAskUserQuestionProgress(
    toolMessage: AgentWidgetMessage,
    progress: {
      answers: Record<string, string | string[]>;
      currentIndex: number;
    }
  ): void {
    const current = this.messages.find((m) => m.id === toolMessage.id);
    if (!current) return;
    this.upsertMessage({
      ...current,
      agentMetadata: {
        ...current.agentMetadata,
        askUserQuestionAnswers: progress.answers,
        askUserQuestionIndex: progress.currentIndex,
      },
    });
  }

  /**
   * Flip an `ask_user_question` tool message from awaiting → answered so
   * render passes stop re-mounting its answer-pill sheet. Idempotent.
   * When `answers` is provided, persists the full structured answer Record
   * atomically with the answered flag: guarding against later events that
   * could re-emit the tool message and clobber the per-pick persisted
   * answers via top-level merge.
   */
  public markAskUserQuestionResolved(
    toolMessage: AgentWidgetMessage,
    answers?: Record<string, string | string[]>
  ): void {
    const current = this.messages.find((m) => m.id === toolMessage.id);
    if (!current) return;
    this.upsertMessage({
      ...current,
      agentMetadata: {
        ...current.agentMetadata,
        awaitingLocalTool: false,
        askUserQuestionAnswered: true,
        ...(answers ? { askUserQuestionAnswers: answers } : {}),
      },
    });
  }

  /**
   * Collect an auto-resolving LOCAL-tool `await` (`webmcp:*` page tools
   * and the built-in `suggest_replies`) into a per-executionId batch
   * and schedule a single deferred flush. Parallel calls (core#3878) emit
   * several `await`s for ONE paused execution within the same stream tick;
   * buffering them and flushing once lets us post ONE `/resume` keyed by the
   * per-call `webMcpToolCallId` rather than racing N name-keyed resumes on the
   * same execution (which 404'd on the second and hung the turn).
   *
   * Deferred via `queueMicrotask` (epoch-guarded) for the same reason the old
   * direct resolve was: handleEvent must return first so the dispatch's
   * `connectStream` sees end-of-stream and releases the shared abortController
   * before a resolve grabs it.
   *
   * Awaits without an `executionId` or `toolCall.id` can't be batched (no key)
   *: route them straight to the single-call path, which surfaces the malformed
   * wire shape via `onError` / an `isError` resume.
   */
  #enqueueWebMcpAwait(toolMessage: AgentWidgetMessage): void {
    const executionId = toolMessage.agentMetadata?.executionId;
    const callId = toolMessage.toolCall?.id;
    if (!executionId || !callId) {
      const queuedEpoch = this.#webMcpEpoch;
      queueMicrotask(() => {
        if (queuedEpoch !== this.#webMcpEpoch) return;
        void this.resolveWebMcpToolCall(toolMessage);
      });
      return;
    }

    let batch = this.webMcpAwaitBatches.get(executionId);
    if (!batch) {
      batch = { snapshots: [], seen: new Set() };
      this.webMcpAwaitBatches.set(executionId, batch);
    }
    // Duplicate await re-emit for a call already in this batch: ignore.
    if (batch.seen.has(callId)) return;
    batch.seen.add(callId);
    batch.snapshots.push(toolMessage);
    // NB: no flush is scheduled here. Flushing happens once the stream that is
    // delivering these awaits ENDS (handleEvent's `status: idle` →
    // scheduleWebMcpBatchFlush). Flushing per-await on the next microtask would
    // race SSE chunk boundaries: two PARALLEL awaits split across separate
    // `read()` chunks would flush the first alone and post a partial resume.
    // Waiting for stream end guarantees every parallel await is collected first.
  }

  /**
   * Flush every buffered local-tool await batch, one `/resume` per executionId.
   * Called once a stream ends (`status: idle` / `error`): by then all parallel
   * `await`s the stream carried have been collected, even if split across
   * SSE chunks. Deferred via `queueMicrotask` (epoch-guarded) so the idle
   * handler returns first and the stream's end-of-stream teardown (streaming /
   * abortController) settles before a resolve grabs them: the same ordering the
   * single-call resolve always relied on.
   */
  #scheduleWebMcpBatchFlush(): void {
    if (this.webMcpAwaitBatches.size === 0) return;
    const queuedEpoch = this.#webMcpEpoch;
    queueMicrotask(() => {
      if (queuedEpoch !== this.#webMcpEpoch) return;
      for (const executionId of [...this.webMcpAwaitBatches.keys()]) {
        this.#flushWebMcpAwaitBatch(executionId);
      }
    });
  }

  /**
   * Run a buffered batch of local-tool awaits for one executionId. Size 1
   * (single call, or distinct-tool turns that happened to arrive alone) takes
   * the original single-call path; size >1 (parallel calls) takes the batched
   * path that posts ONE `/resume`. The batch is removed from the map up front
   * so any later sibling re-emit (e.g. from a re-pause) forms a fresh batch
   * rather than mutating one already in flight.
   */
  #flushWebMcpAwaitBatch(executionId: string): void {
    const batch = this.webMcpAwaitBatches.get(executionId);
    if (!batch) return;
    this.webMcpAwaitBatches.delete(executionId);
    const { snapshots } = batch;
    if (snapshots.length === 1) {
      void this.resolveWebMcpToolCall(snapshots[0]);
    } else if (snapshots.length > 1) {
      void this.#withActions((actions, host) =>
        actions.resolveWebMcpToolCallBatch(host, executionId, snapshots)
      );
    }
  }

  /**
   * Tear down every in-flight WebMCP resolve and advance the epoch. Each
   * resolve owns a dedicated AbortController (chained/parallel resolves don't
   * share one), so we abort them individually; the aborts propagate into the
   * bridge's execute race and into each `/resume` fetch signal. Bumping
   * `webMcpEpoch` strands any resolve still deferred in a queued microtask:   * it captured the prior epoch and bails before installing a fresh
   * controller, so it can't escape this teardown. Called from every stop /
   * new-turn boundary (cancel, clearMessages, hydrateMessages, sendMessage).
   */
  #abortWebMcpResolves(): void {
    for (const controller of this.webMcpResolveControllers) {
      controller.abort();
    }
    this.webMcpResolveControllers.clear();
    // Settle every approval bubble still awaiting a click. The bridge parks a
    // resolve on `await requestConfirm(...)` (→ requestWebMcpApproval) and only
    // re-checks `signal.aborted` AFTER that await returns: aborting the
    // controller above does NOT unblock it. Left unsettled, the bridge's
    // execute(), its `/resume`, and the resolve's `finally` would all hang
    // forever (and the resolver map would leak across teardowns). Route through
    // `resolveWebMcpApproval(…, "denied")` so each parked Promise resolves
    // `false` AND its bubble message flips out of `pending` (no stale "Approve/
    // Deny" left clickable). The bridge then returns cleanly and its
    // post-confirm `signal.aborted` guard bails before any host-page side effect
    // or stale `/resume`. Snapshot the keys first: resolveWebMcpApproval
    // mutates the map as it deletes each resolver.
    for (const approvalMessageId of [...this.webMcpApprovalResolvers.keys()]) {
      this.resolveWebMcpApproval(approvalMessageId, "denied");
    }
    // Drop any awaits buffered for a not-yet-flushed batch: their messages are
    // being torn down, and a microtask-deferred flush must not survive. The
    // epoch bump below also strands an already-scheduled flush.
    this.webMcpAwaitBatches.clear();
    this.#webMcpEpoch++;
  }

  public cancel() {
    this.client.cancelStreams();
    this.abortController?.abort();
    this.abortController = null;
    // A user stop also cancels any pending/in-flight durable reconnect and
    // clears the resume handle (the abort above already killed its fetch).
    this.#teardownReconnect();
    // Tear down every in-flight WebMCP resolve (each owns its own controller,
    // independent of the shared one above). Clear the inflight set so retries
    // are possible if the user re-issues the same await context.
    this.#abortWebMcpResolves();
    this.#actionsEpoch++;
    this.webMcpInflightKeys.clear();
    // Stop any in-progress audio too: when the user hits "stop", they want
    // the assistant to actually stop talking, not just stop generating tokens.
    // Both helpers are safe no-ops when audio isn't configured.
    this.stopSpeaking();
    this.stopVoicePlayback();
    this.#setStreaming(false);
    this.#setStatus("idle");
    this.#finishRun("cancel");
  }

  public clearMessages() {
    this.client.cancelStreams();
    this.#clearBackgroundConversations();
    this.stopSpeaking();
    this.abortController?.abort();
    this.abortController = null;
    this.#teardownReconnect();
    // Tear down every in-flight WebMCP resolve too: their messages are about
    // to be wiped, and a microtask-deferred resolve must not survive the clear.
    this.#abortWebMcpResolves();
    this.#actionsEpoch++;
    this.#runActivity = undefined;
    this.messages = [];
    this.agentExecution = null;
    this.#clearArtifactState();
    // Clearing messages also wipes the WebMCP dedupe state: a fresh
    // conversation should not refuse to call a webmcp:* tool just because
    // a tool with the same key resolved in the prior conversation.
    this.webMcpInflightKeys.clear();
    this.webMcpResolvedKeys.clear();
    // A fresh conversation must resend the full WebMCP tool list on its next
    // turn: drop the diff-only fingerprint cache (server keys by recordId, so
    // a new conversation has no stored set to match).
    this.client.resetClientToolsFingerprint();
    this.#setStreaming(false);
    this.#setStatus("idle");
    this.callbacks.onMessagesChanged([...this.messages]);
    this.#finishRun("cancel");
  }

  public getArtifacts(): PersonaArtifactRecord[] {
    return [...this.artifacts.values()];
  }

  public getArtifactById(id: string): PersonaArtifactRecord | undefined {
    return this.artifacts.get(id);
  }

  public getSelectedArtifactId(): string | null {
    return this.#selectedArtifactId;
  }

  public selectArtifact(id: string | null): void {
    this.#selectedArtifactId = id;
    this.#emitArtifactsState();
  }

  public clearArtifacts(): void {
    this.#clearArtifactState();
  }

  public upsertArtifact(manual: PersonaArtifactManualUpsert): PersonaArtifactRecord {
    const id =
      manual.id ||
      `art_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    const rec: PersonaArtifactRecord =
      manual.artifactType === "markdown"
        ? {
            id,
            artifactType: "markdown",
            title: manual.title,
            status: "complete",
            markdown: manual.content,
            ...(manual.file ? { file: manual.file } : {}),
            ...(manual.presentation ? { presentation: manual.presentation } : {})
          }
        : {
            id,
            artifactType: "component",
            title: manual.title,
            status: "complete",
            component: manual.component,
            props: manual.props ?? {},
            ...(manual.presentation ? { presentation: manual.presentation } : {})
          };
    this.artifacts.set(id, rec);
    this.#selectedArtifactId = id;
    this.#emitArtifactsState();
    if (manual.transcript !== false) {
      this.#injectArtifactRefBlock(rec);
    }
    return rec;
  }

  /**
   * Injects (or refreshes) the in-thread artifact block for a programmatically
   * upserted artifact, matching the streamed UX: the resolved display mode
   * picks the component ("collapsed"/"panel" → reference card, "inline" → inline
   * preview).
   *
   * Unlike the streamed path (which embeds content on `artifact_complete`),
   * the programmatic record is complete up front, so the final markdown /
   * file meta / component name + props are embedded immediately and the block
   * hydrates after a refresh without the original registry state.
   *
   * A re-upsert of the same id rebuilds the existing block's persisted
   * rawContent in place (the registry is not persisted, so hydration reads
   * these props); without this the block would keep the first version's
   * content after a refresh.
   */
  #injectArtifactRefBlock(rec: PersonaArtifactRecord): void {
    const refId = `artifact-ref-${rec.id}`;
    const displayMode = resolveArtifactDisplayMode(
      this.config.features?.artifacts,
      rec
    );
    const rawContent = buildArtifactRefRawContent(displayMode, {
      artifactId: rec.id,
      title: rec.title,
      artifactType: rec.artifactType,
      status: "complete",
      ...(rec.file ? { file: rec.file } : {}),
      ...(rec.presentation ? { presentation: rec.presentation } : {}),
      ...(rec.component ? { component: rec.component } : {}),
      ...(rec.props ? { componentProps: rec.props } : {}),
      ...(rec.markdown !== undefined ? { markdown: rec.markdown } : {})
    });
    // Re-upsert: mutate the STORED message (inject stores a copy via
    // ensureSequence, so a local copy would be lost) and notify so the
    // transcript re-renders and persistence picks up the new version.
    const existing = this.messages.find((m) => m.id === refId);
    if (existing) {
      existing.rawContent = rawContent;
      existing.streaming = false;
      this.callbacks.onMessagesChanged([...this.messages]);
      return;
    }
    this.injectAssistantMessage({
      id: refId,
      content: "",
      rawContent
    });
  }

  /**
   * Re-materialize existing transcript artifact blocks after a live display
   * preference update. One messages callback keeps conversion atomic.
   */
  #refreshArtifactReferenceBlocks(): void {
    let changed = false;
    for (const rec of this.artifacts.values()) {
      const message = this.messages.find((candidate) => candidate.id === `artifact-ref-${rec.id}`);
      if (!message) continue;
      const displayMode = resolveArtifactDisplayMode(
        this.config.features?.artifacts,
        rec
      );
      const rawContent = buildArtifactRefRawContent(displayMode, {
        artifactId: rec.id,
        title: rec.title,
        artifactType: rec.artifactType,
        status: rec.status,
        ...(rec.file ? { file: rec.file } : {}),
        ...(rec.presentation ? { presentation: rec.presentation } : {}),
        ...(rec.component ? { component: rec.component } : {}),
        ...(rec.props ? { componentProps: rec.props } : {}),
        ...(rec.markdown !== undefined ? { markdown: rec.markdown } : {}),
      });
      if (message.rawContent === rawContent) continue;
      message.rawContent = rawContent;
      message.streaming = rec.status === "streaming";
      changed = true;
    }
    if (changed) this.callbacks.onMessagesChanged([...this.messages]);
  }

  #clearArtifactState(): void {
    if (this.artifacts.size === 0 && this.#selectedArtifactId === null) return;
    this.artifacts.clear();
    this.#selectedArtifactId = null;
    this.#emitArtifactsState();
  }

  #emitArtifactsState(): void {
    this.callbacks.onArtifactsState?.({
      artifacts: [...this.artifacts.values()],
      selectedId: this.#selectedArtifactId
    });
  }

  #applyArtifactStreamEvent(ev: AgentWidgetEvent): void {
    switch (ev.type) {
      case "artifact_start": {
        if (ev.artifactType === "markdown") {
          this.artifacts.set(ev.id, {
            id: ev.id,
            artifactType: "markdown",
            title: ev.title,
            status: "streaming",
            markdown: "",
            ...(ev.file ? { file: ev.file } : {})
          });
        } else {
          this.artifacts.set(ev.id, {
            id: ev.id,
            artifactType: "component",
            title: ev.title,
            status: "streaming",
            component: ev.component ?? "",
            props: {}
          });
        }
        this.#selectedArtifactId = ev.id;
        break;
      }
      case "artifact_delta": {
        const row = this.artifacts.get(ev.id);
        if (row?.artifactType === "markdown") {
          row.markdown = (row.markdown ?? "") + ev.artDelta;
        }
        break;
      }
      case "artifact_update": {
        const row = this.artifacts.get(ev.id);
        if (row?.artifactType === "component") {
          row.props = { ...row.props, ...ev.props };
          if (ev.component) row.component = ev.component;
        }
        break;
      }
      case "artifact_complete": {
        const row = this.artifacts.get(ev.id);
        if (row) row.status = "complete";
        break;
      }
      default:
        return;
    }
    this.#emitArtifactsState();
  }

  public hydrateMessages(messages: AgentWidgetMessage[]) {
    this.client.cancelStreams();
    this.abortController?.abort();
    this.abortController = null;
    // Hydration replaces the conversation: also cancel any pending reconnect and
    // clear the resume handle (a boot resume re-arms via resumeFromHandle after).
    this.#teardownReconnect();
    // Hydration replaces the conversation: abort and forget every in-flight
    // WebMCP resolve; their messages are about to be replaced.
    this.#abortWebMcpResolves();
    this.#actionsEpoch++;
    // Wipe the WebMCP dedupe state alongside the message restore: the
    // incoming snapshot is treated as a fresh conversation context.
    this.webMcpInflightKeys.clear();
    this.webMcpResolvedKeys.clear();
    this.#runActivity = undefined;
    this.messages = this.#sortMessages(
      messages.map((message) => ({
        ...restoreBrowserToolState(message),
        streaming: false,
        sequence: message.sequence ?? this.#nextSequence()
      }))
    );
    this.#setStreaming(false);
    this.#setStatus("idle");
    this.callbacks.onMessagesChanged([...this.messages]);
    this.#finishRun("cancel");
  }

  public hydrateArtifacts(
    artifacts: PersonaArtifactRecord[],
    selectedId: string | null = null,
    preserveStatus = false
  ) {
    this.artifacts.clear();
    for (const rec of artifacts) {
      this.artifacts.set(rec.id, { ...rec, status: preserveStatus ? rec.status : "complete" });
    }
    this.#selectedArtifactId = selectedId;
    this.#emitArtifactsState();
  }

  private handleEvent = (event: AgentWidgetEvent, readOnlyReplay = false) => {
    if (this.#backgroundCommitEvents) {
      this.#backgroundCommitEvents.push({ event, readOnlyReplay });
      return;
    }
    const delegation = this.#voiceDelegation;
    let delegated = false;
    if (delegation) {
      if (event.type === "error" || (event.type === "status" && event.status === "error")) {
        delegation.failed = true;
      } else if (event.type === "message" && event.message.role === "assistant") {
        // The voice model reads this answer aloud: browser TTS must skip it,
        // unless the server dropped the result (then nobody speaks it).
        if (!delegation.dropped) this.ttsSpokenMessageIds.add(event.message.id);
        if (!delegation.ids.includes(event.message.id)) delegation.ids.push(event.message.id);
        delegated = true;
      }
    }
    if (event.type === "message") {
      // Only incoming run events establish ownership, before rendering.
      if (this.#activeRun && !readOnlyReplay && event.message.role === "assistant") this.#runActivity?.messageIds.add(event.message.id);
      this.upsertMessage(event.message);
      if (event.message.agentMetadata?.awaitingLocalTool ||
          (event.message.variant === "approval" && event.message.approval?.status === "pending")) {
        this.#emitConversationExecutionState("needs-attention");
      }
      // The delegated answer grew: a streaming voice bridge reads it now.
      if (delegated) delegation!.onUpdate?.();

      // A resolved approval reaches this handler only from the server's
      // `approval_complete` (resolveApproval's optimistic update upserts
      // directly). The server may now finish the paused call itself on this
      // stream, so the bubble settles at stream end only if it is still open.
      if (
        event.message.variant === "approval" &&
        event.message.approval &&
        event.message.approval.status !== "pending"
      ) {
        this.#approvalSettlesAtStreamEnd.set(event.message.id, this.#activeStreamToken);
      }

      // Track the open assistant text bubble's REAL streamed id so a durable
      // reconnect keeps filling the same message. The proxy path auto-generates
      // this id (it isn't the session's pre-generated one), so we must read it
      // off the actual message events, not assume it.
      if (
        event.message.role === "assistant" &&
        !event.message.variant &&
        event.message.streaming
      ) {
        this.#activeAssistantMessageId = event.message.id;
      }

      // Local-tool auto-resolve: when a await emits a tool-variant
      // message for a `webmcp:*` tool, or the built-in fire-and-forget
      // `suggest_replies`: resolve it and post the result to /resume.
      // Unlike ask_user_question, no user pill click is required; for WebMCP
      // the bridge's confirm bubble is the only interactive surface, and
      // suggest_replies resumes with a canned "shown" result while the chips
      // render above the composer.
      //
      // Defer via `queueMicrotask` so handleEvent returns FIRST. The current
      // SSE consumer is still mid-loop; once we return, the dispatch's
      // `connectStream` sees end-of-stream (server closes the SSE at
      // await), flips status to "idle", and clears `abortController`
      // before our resolve grabs them. Without this, the original dispatch's
      // finalizer would clobber the new abort controller and `streaming=true`
      // set inside `resolveWebMcpToolCall`.
      //
      // ALWAYS resolve when the wire name carries the `webmcp:` prefix, even
      // if the bridge is non-operational. Otherwise the dispatch stays paused
      // indefinitely: `resolveWebMcpToolCall` translates the missing-bridge
      // case into an isError result that resumes the flow cleanly.
      // `suggest_replies`, by contrast, is gated on its feature flag: when
      // the resolved `enabled` is false the widget neither renders chips nor
      // resumes: the same parked-execution posture as a server-declared
      // ask_user_question with its sheet disabled.
      // The agent paused for an approval or a local tool: warm the resolve
      // paths (the lazy `session-actions.js` chunk in the IIFE build) so the
      // resume doesn't wait on a fetch.
      if (
        event.message.variant === "approval" ||
        event.message.agentMetadata?.awaitingLocalTool === true
      ) {
        loadSessionActions().catch(() => {});
      }

      const tc = event.message.toolCall;
      const autoResolvable =
        !!tc?.name &&
        (isWebMcpToolName(tc.name) ||
          (tc.name === SUGGEST_REPLIES_TOOL_NAME &&
            resolveFollowUpsFeature(this.config).enabled));
      if (
        event.message.agentMetadata?.awaitingLocalTool === true &&
        autoResolvable && !readOnlyReplay &&
        this.messages.find(message => message.id === event.message.id)
          ?.agentMetadata?.awaitingLocalTool === true
      ) {
        // Collect the await into its executionId's batch instead of resolving
        // it on the spot. Parallel same-tool calls (core#3878) arrive as
        // separate `await`s in the same stream; batching lets us post ONE
        // `/resume` keyed by per-call id (see `enqueueWebMcpAwait`).
        this.#enqueueWebMcpAwait(event.message);
      }

      // Track agent execution state from message metadata
      if (event.message.agentMetadata?.executionId) {
        if (!this.agentExecution || this.agentExecution.executionId !== event.message.agentMetadata.executionId) {
          this.agentExecution = {
            executionId: event.message.agentMetadata.executionId,
            agentId: '',
            agentName: event.message.agentMetadata.agentName ?? '',
            status: 'running',
            currentIteration: event.message.agentMetadata.iteration ?? 0,
            maxTurns: 0
          };
        } else if (event.message.agentMetadata.iteration !== undefined) {
          this.agentExecution.currentIteration = event.message.agentMetadata.iteration;
        }
      }
    } else if (event.type === "cursor") {
      // Durable-reconnect cursor. Track the highest SSE `id:` seq as the resume
      // cursor and (lazily) form the resume handle once the executionId is
      // known. Only durable, resumable executions emit these, so this is the
      // natural gate: no `id:` lines → no handle → reconnect never arms.
      this.#streamCheckpoint = event.checkpoint;
      this.#trackCursor(event.id);
    } else if (event.type === "status") {
      if (event.executionId) {
        this.agentExecution = {
          executionId: event.executionId, agentId: "", agentName: "",
          status: "running", currentIteration: 0, maxTurns: 0,
        };
        this.#emitConversationExecutionState("running");
      }
      // A plain `idle` (no `terminal`) on the durable lane, while the run is
      // genuinely mid-turn, is a dropped connection, not a finish and not an
      // intentional `await` pause. Reconnect instead of finalizing.
      if (event.status === "idle" && !event.terminal && this.#isDurableDrop()) {
        this.#beginReconnect();
        return;
      }
      if (event.status === "error") this.#emitConversationExecutionState("failed");
      else if (event.terminal && event.status === "idle") this.#emitConversationExecutionState("completed");
      this.#setStatus(event.status);
      if (event.status === "connecting") {
        this.#setStreaming(true);
      } else if (event.status === "idle" || event.status === "error") {
        // A client-tool pause is an intentional stream end but NOT a terminal:
        // retain its cursor so `/client/resume` can attach after the await
        // frame instead of replaying the whole turn. Real terminals and
        // ordinary non-durable idles still clear the handle.
        if (!this.#isAwaitPending()) this.#clearResumable();
        // Keep the typing indicator up while a WebMCP resolve is still in
        // flight: in a chained turn the intermediate resume stream ends with an
        // idle status, but the successor tool is still executing. The resolve's
        // own `finally` flips streaming off once the resolve set drains.
        if (this.webMcpResolveControllers.size === 0) {
          this.#setStreaming(false);
          this.abortController = null;
          // The turn mutated the record and the chat response carries no
          // revision; finalize any browser-only projection right after.
          this.#invalidateConversationRevision();
          this.#scheduleProjectionFinalization();
        }
        // Mark agent execution as complete when streaming ends: UNLESS local
        // tools are still outstanding. A batched WebMCP resume is deferred to
        // the microtask below (so `webMcpResolveControllers` is still empty
        // here) and a chained resolve may be mid-flight; marking the run
        // 'complete' now would make isAgentRunning() report a finished run while
        // page tools are still executing. Stay 'running': the resume stream's
        // own idle (with batches drained and resolves settled) marks it done.
        const webMcpPending =
          this.webMcpAwaitBatches.size > 0 ||
          this.webMcpResolveControllers.size > 0;
        if (this.agentExecution?.status === 'running') {
          if (event.status === "error") {
            this.agentExecution.status = 'error';
          } else if (!webMcpPending) {
            this.agentExecution.status = 'complete';
          }
        }
        // The stream that delivered any local-tool `await`s has now ended,
        // so every parallel await it carried is collected. Flush them as ONE
        // batched `/resume` per executionId (deferred: see
        // scheduleWebMcpBatchFlush). Runs AFTER the teardown above so a resolve
        // doesn't fight the end-of-stream streaming/abortController reset.
        this.#scheduleWebMcpBatchFlush();
      }
    } else if (event.type === "error") {
      this.#emitConversationExecutionState("failed");
      this.#setStatus("error");
      // Terminal error: drop the resume handle so no reconnect arms.
      this.#clearResumable();
      // Mirror the idle/status handler: don't tear down streaming while a
      // WebMCP resolve is still confirming/executing on another stream: an
      // error on one chained resume stream must not hide the typing indicator
      // (or null a controller) for a sibling/successor resolve still in flight.
      // The resolve's own `finally` flips streaming off once the set drains.
      if (this.webMcpResolveControllers.size === 0) {
        this.#setStreaming(false);
        this.abortController = null;
      }
      if (this.agentExecution?.status === 'running') {
        this.agentExecution.status = 'error';
      }
      this.callbacks.onError?.(event.error);
    } else if (
      event.type === "artifact_start" ||
      event.type === "artifact_delta" ||
      event.type === "artifact_update" ||
      event.type === "artifact_complete"
    ) {
      this.#applyArtifactStreamEvent(event);
    }
  };

  // ── Durable-session reconnect ──────────────────────────────────

  /** Validate before any navigation or composer state changes. */
  public assertBackgroundSwitchable(): void {
    const adapter = this.config.backgroundConversations;
    if (adapter?.capabilities?.durableExecution !== true ||
        adapter.capabilities?.readOnlyReplay !== true || typeof adapter.reconnectStream !== "function") {
      throw new SessionHistoryError("background_unavailable", "This backend does not support background conversations");
    }
    if (this.config.parseSSEEvent) {
      throw new SessionHistoryError("background_unavailable", "Background replay requires the native event parser");
    }
    if (this.#isAwaitPending() || this.webMcpResolveControllers.size || this.webMcpAwaitBatches.size ||
        ((this.streaming || this.#reconnecting) && (!this.#resumable || !this.#activeConversationId))) {
      throw new SessionHistoryError("conversation_busy", "This turn cannot safely continue in the background");
    }
  }

  async #loadBackgroundController(): Promise<void> {
    if (this.#backgroundController) return;
    const adapter = this.config.backgroundConversations!;
    const epoch = this.#historyContextEpoch;
    const { BackgroundConversationController } = await loadBackgroundRuntime();
    if (epoch !== this.#historyContextEpoch || adapter !== this.config.backgroundConversations) {
      throw new SessionHistoryError("superseded", "The history identity changed");
    }
    if (this.#backgroundController) return;
    this.#backgroundController = new BackgroundConversationController(adapter, (state) => {
      this.#publishConversationExecutionState(state);
    });
    let subscribing = true;
    this.#backgroundIdentityUnsubscribe = this.#historyProvider?.subscribeIdentityStatus(() => {
      if (subscribing) return;
      this.#backgroundController?.clear();
      // An identity change also invalidates any active replay's authorization.
      this.cancel();
    }) ?? null;
    subscribing = false;
  }

  #clearBackgroundConversations(): void {
    this.#backgroundController?.clear();
    this.#backgroundController = null;
    this.#backgroundIdentityUnsubscribe?.();
    this.#backgroundIdentityUnsubscribe = null;
  }

  #emitConversationExecutionState(status: ConversationExecutionState["status"]): void {
    const executionId = this.#resumable?.executionId ?? this.agentExecution?.executionId;
    const conversationId = this.#activeConversationId;
    if (!this.config.backgroundConversations || !executionId || !conversationId) return;
    const state: ConversationExecutionState = { conversationId, executionId, status, detached: false };
    this.#publishConversationExecutionState(state);
  }

  #publishConversationExecutionState(state: ConversationExecutionState): void {
    // Observers cannot turn a successful navigation into a partial commit.
    try { this.config.onConversationExecutionState?.(state); } catch { /* Host observer. */ }
    try { this.callbacks.onConversationExecutionState?.(state); } catch { /* Host observer. */ }
  }

  #getReconnectStream(): AgentWidgetConfig["reconnectStream"] {
    const adapter = this.config.backgroundConversations;
    const conversationId = this.#activeConversationId;
    return adapter && conversationId
      ? (ctx) => adapter.reconnectStream({ ...ctx, conversationId })
      : this.config.reconnectStream;
  }

  #restoreBackgroundCheckpoint(checkpoint: BackgroundCheckpoint): void {
    this.#beginRun(checkpoint.handle.executionId);
    this.#activeAssistantMessageId = checkpoint.handle.assistantMessageId;
    const assistant = this.messages.find((message) => message.id === this.#activeAssistantMessageId);
    if (assistant) assistant.streaming = checkpoint.streamCheckpoint?.openAssistant ?? true;
    this.#streamCheckpoint = checkpoint.streamCheckpoint;
    this.agentExecution = {
      executionId: checkpoint.handle.executionId, agentId: "", agentName: "",
      status: "running", currentIteration: 0, maxTurns: 0,
    };
    this.#resumable = checkpoint.handle;
    this.#notifyExecutionState(true);
    this.#emitConversationExecutionState(checkpoint.observedStatus ?? "running");
    this.#beginReconnect();
  }

  /**
   * Record the latest SSE `id:` cursor and (lazily) form/advance the resume
   * handle. Needs an executionId (lifted into `agentExecution` from the same
   * frame's metadata, handled before this trailing cursor event) and the active
   * assistant message id.
   */
  #trackCursor(id: string): void {
    const executionId = this.agentExecution?.executionId;
    if (!executionId || !this.#activeAssistantMessageId) return;
    // Only track while the run is live. A graceful terminal sets the execution
    // to 'complete'/'error' and clears the handle BEFORE its own frame's
    // trailing cursor fires; without this guard that cursor would re-arm the
    // handle and the next plain `idle` would look like a spurious drop.
    if (this.agentExecution?.status !== "running") return;
    const isNew = this.#resumable === null;
    this.#resumable = {
      executionId,
      lastEventId: id,
      assistantMessageId: this.#activeAssistantMessageId,
      status: "running",
    };
    // Fire immediately when the handle first appears (so the host can persist
    // the executionId promptly); throttle subsequent cursor advances.
    this.#notifyExecutionState(isNew);
    if (isNew) this.#emitConversationExecutionState("running");
  }

  /**
   * The drop gate: is this stream-end a dropped connection we should reconnect,
   * rather than a graceful finish or an intentional `await` pause? ALL must
   * hold (see plan §Design overview keystone 3).
   */
  #isDurableDrop(): boolean {
    return (
      this.#resumable !== null &&
      typeof this.#getReconnectStream() === "function" &&
      this.abortController?.signal.aborted !== true &&
      this.webMcpResolveControllers.size === 0 &&
      this.webMcpAwaitBatches.size === 0 &&
      !this.#isAwaitPending()
    );
  }

  /**
   * True when the run is parked awaiting user input (an unanswered
   * `ask_user_question` / local-tool await, or a pending approval). Those end
   * the stream with `idle` and no terminal too, but are NOT drops.
   */
  #isAwaitPending(): boolean {
    return this.messages.some((m) => {
      if (
        m.agentMetadata?.awaitingLocalTool === true &&
        m.agentMetadata?.askUserQuestionAnswered !== true
      ) {
        return true;
      }
      if (m.variant === "approval" && m.approval?.status === "pending") {
        return true;
      }
      return false;
    });
  }

  /**
   * Enter the reconnect flow. Keeps the assistant bubble open and `streaming`
   * true, flips to `resuming`, and kicks the bounded backoff loop. Re-entrant
   * calls (a second drop while reconnecting) are no-ops.
   */
  #beginReconnect(): void {
    if (this.#reconnecting) return;
    if (!this.#resumable || typeof this.#getReconnectStream() !== "function") {
      return;
    }
    // Flip the visible state synchronously — the bubble stays open and a
    // `resuming` status is observable immediately (e.g. right after
    // `resumeFromHandle`). The heavy backoff loop is loaded lazily below.
    this.#reconnecting = true;
    this.callbacks.onReconnect?.({ phase: "paused", handle: this.#resumable });
    this.#setStreaming(true);
    this.#setStatus("resuming");
    void this.#loadReconnectController().then((controller) => {
      // The run may have been torn down (new turn / cancel / hydrate) while the
      // module was loading; only start the loop if we're still reconnecting.
      if (this.#reconnecting && this.#resumable) controller.begin();
    });
  }

  /**
   * Lazily import and instantiate the reconnect controller. Cached so repeated
   * reconnects reuse one controller (and one module load). Deliberately kept off
   * every hot path (`sendMessage` / `cancel` / `teardownReconnect`) so a widget
   * that never reconnects never pulls the chunk.
   */
  #loadReconnectController(): Promise<ReconnectController> {
    if (this.#reconnectController) {
      return Promise.resolve(this.#reconnectController);
    }
    if (!this.#reconnectControllerPromise) {
      this.#reconnectControllerPromise = loadSessionReconnect().then(
        ({ createReconnectController }) => {
          const controller = createReconnectController(
            this.#buildReconnectHost()
          );
          this.#reconnectController = controller;
          return controller;
        }
      );
      // A failed chunk fetch must not poison the memoized promise: clear it so
      // the next reconnect trigger retries the load.
      this.#reconnectControllerPromise.catch(() => {
        this.#reconnectControllerPromise = null;
      });
    }
    return this.#reconnectControllerPromise;
  }

  /** The narrow surface the lazily-loaded reconnect loop drives. */
  #buildReconnectHost(): ReconnectHost {
    const session = this;
    return {
      get config() {
        return { ...session.config, reconnectStream: session.#getReconnectStream() };
      },
      getResumable: () => session.#resumable,
      clearResumable: () => session.#clearResumable(),
      getStatus: () => session.status,
      setStatus: (status) => session.#setStatus(status),
      setStreaming: (streaming) => session.#setStreaming(streaming),
      setReconnecting: (value) => {
        session.#reconnecting = value;
      },
      setAbortController: (controller) => {
        session.abortController = controller;
      },
      getMessages: () => session.messages,
      isAwaitPending: () => session.#isAwaitPending(),
      notifyMessagesChanged: () =>
        session.callbacks.onMessagesChanged([...session.messages]),
      resumeConnect: (body, assistantMessageId, seedContent) =>
        session.connectStream(body, {
          assistantMessageId,
          allowReentry: true,
          preserveAssistantId: true,
          seedContent,
          readOnlyReplay: !!session.config.backgroundConversations,
        }),
      appendMessage: (message) => session.#appendMessage(message),
      settleParked: (executionId) => session.#settleParkedReconnect(executionId),
      nextSequence: () => session.#nextSequence(),
      emitReconnect: (event) => session.callbacks.onReconnect?.(event),
      buildErrorContent: (message) =>
        buildDispatchErrorContent(
          new Error(message),
          session.config.errorMessage
        ),
      onError: (error) => session.callbacks.onError?.(error),
    };
  }

  /** Public manual retry (e.g. a "Reconnect" button). */
  public reconnectNow(): void {
    if (this.#reconnecting) {
      // Already trying, so just short-circuit the current backoff. (No-op if the
      // controller is still loading; the first attempt hasn't slept yet.)
      this.#reconnectController?.wake();
      return;
    }
    this.#beginReconnect();
  }

  /**
   * Resume a durable turn on boot (tab-reload path). Re-opens the trailing
   * assistant bubble (or mints one), restores the execution/handle, and
   * reconnects from the persisted cursor. No-op without `reconnectStream`.
   */
  public resumeFromHandle(resume: { executionId: string; after: string }): void {
    if (typeof this.config.reconnectStream !== "function") return;
    if (this.#reconnecting) return;
    this.#beginRun(resume.executionId);

    // A run parked on an await the restored transcript can still answer (an
    // ask_user_question card, a server approval) has no stream to replay, and
    // reconnecting would spin into a "Connection lost" bubble. Keep the cursor
    // so the answer's `/resume` attaches after the await frame, as in-session.
    if (this.#isAwaitPending()) {
      this.#adoptBootExecution(resume.executionId);
      this.#resumable = {
        executionId: resume.executionId,
        lastEventId: resume.after,
        assistantMessageId: this.#activeAssistantMessageId ?? "",
        status: "running",
      };
      return;
    }

    // Parked on a WebMCP call the reload interrupted: still reconnect once, so
    // a replayed `await` re-dispatches the call on this page, but don't reopen
    // the pre-tool text bubble or paint an empty one. The reconnect loop ends
    // the turn quietly when nothing replays (`#settleParkedReconnect`).
    let assistantId = this.#hasInterruptedBrowserTool(resume.executionId)
      ? generateAssistantMessageId()
      : this.#reopenTrailingAssistant();
    if (!assistantId) {
      assistantId = generateAssistantMessageId();
      this.#appendMessage({
        id: assistantId,
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
        streaming: true,
        sequence: this.#nextSequence(),
      });
    }
    this.#activeAssistantMessageId = assistantId;
    this.#adoptBootExecution(resume.executionId);
    this.#resumable = {
      executionId: resume.executionId,
      lastEventId: resume.after,
      assistantMessageId: assistantId,
      status: "running",
    };
    this.#beginReconnect();
  }

  /**
   * A reconnect attempt left the run parked rather than dropped: the turn is
   * waiting on the visitor or a page tool (keep the cursor, as an in-session
   * await pause does), or on a WebMCP call the reload interrupted (nothing can
   * answer it, so drop the handle). Either way stop reconnecting without a
   * failure bubble. Returns false for an ordinary drop, which keeps retrying.
   */
  #settleParkedReconnect(executionId: string): boolean {
    const pageToolBusy =
      this.webMcpResolveControllers.size > 0 || this.webMcpAwaitBatches.size > 0;
    if (!pageToolBusy && !this.#isAwaitPending()) {
      if (!this.#hasInterruptedBrowserTool(executionId)) return false;
      this.#clearResumable();
    }
    this.#reconnecting = false;
    if (!pageToolBusy) {
      let changed = false;
      for (const msg of this.messages) {
        if (msg.streaming) {
          msg.streaming = false;
          changed = true;
        }
      }
      if (changed) this.callbacks.onMessagesChanged([...this.messages]);
      this.#setStreaming(false);
      this.abortController = null;
    }
    this.#setStatus("idle");
    return true;
  }

  /** Restore the running execution a boot resume refers to, if none is live. */
  #adoptBootExecution(executionId: string): void {
    if (this.agentExecution) return;
    this.agentExecution = {
      executionId,
      agentId: "",
      agentName: "",
      status: "running",
      currentIteration: 0,
      maxTurns: 0,
    };
  }

  /**
   * True when restore settled one of this execution's WebMCP calls as
   * interrupted (see `restoreBrowserToolState`): the run is parked on a
   * browser callback that no longer exists.
   */
  #hasInterruptedBrowserTool(executionId: string): boolean {
    return this.messages.some(
      (m) =>
        m.agentMetadata?.executionId === executionId &&
        [m.toolCall, ...(m.tools ?? [])].some((tool) => tool?.interrupted === true)
    );
  }

  /**
   * Reopen the trailing assistant bubble (the persisted partial) so replayed
   * deltas append to it. Returns its id, or null if the last turn doesn't end
   * in a plain assistant message.
   */
  #reopenTrailingAssistant(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const m = this.messages[i];
      if (m.role === "assistant" && !m.variant) {
        m.streaming = true;
        this.callbacks.onMessagesChanged([...this.messages]);
        return m.id;
      }
      if (m.role === "user") break;
    }
    return null;
  }

  /**
   * Tear down any in-flight/pending reconnect and clear the resume handle. Runs
   * on every new turn / cancel / hydrate, so it must NOT pull the reconnect
   * chunk: it only delegates to the controller if one was already created.
   */
  #teardownReconnect(): void {
    this.#reconnecting = false;
    this.#reconnectController?.teardown();
    this.#clearResumable();
  }

  /** Drop the resume handle and notify the host (`onExecutionState(null)`). */
  #clearResumable(): void {
    if (this.#executionStateTimer) {
      clearTimeout(this.#executionStateTimer);
      this.#executionStateTimer = null;
    }
    const had = this.#resumable !== null;
    this.#resumable = null;
    this.#streamCheckpoint = undefined;
    if (had) this.config.onExecutionState?.(null);
  }

  /**
   * Surface the resume handle to the host for persistence. Immediate on
   * create/clear; trailing-edge throttled on cursor advances so it isn't called
   * per delta.
   */
  #notifyExecutionState(immediate: boolean): void {
    const cb = this.config.onExecutionState;
    if (!cb) return;
    if (immediate) {
      if (this.#executionStateTimer) {
        clearTimeout(this.#executionStateTimer);
        this.#executionStateTimer = null;
      }
      cb(this.#resumable);
      return;
    }
    if (this.#executionStateTimer) return; // a trailing call is already queued
    this.#executionStateTimer = setTimeout(() => {
      this.#executionStateTimer = null;
      this.config.onExecutionState?.(this.#resumable);
    }, 500);
  }

  /** The current durable resume handle, if any (read-only). */
  public getResumableHandle(): ResumableHandle | null {
    return this.#resumable;
  }

  #setStatus(status: AgentWidgetSessionStatus) {
    if (status === "error") {
      this.#runFailed = true;
      this.#scheduleRunFinish();
    }
    if (this.status === status) return;
    this.status = status;
    this.callbacks.onStatusChanged(status);
  }

  #setStreaming(streaming: boolean) {
    if (!streaming) this.#scheduleRunFinish();
    if (this.streaming === streaming) return;
    const wasStreaming = this.streaming;
    this.streaming = streaming;
    this.callbacks.onStreamingChanged(streaming);

    // Speak the latest assistant message when streaming completes
    if (wasStreaming && !streaming) {
      this.#flushApprovalSettles(undefined);
      this.speakLatestAssistantMessage();
    }
  }

  /**
   * Speak the latest assistant message using the Web Speech API
   * if text-to-speech is enabled in the config.
   */
  /** `only`: speak it only if the latest assistant message is one of these. */
  private speakLatestAssistantMessage(only?: string[]) {
    const ttsConfig = this.config.textToSpeech;
    if (!ttsConfig?.enabled) return;

    // Determine if browser TTS should fire:
    // - provider 'browser' (or unset): always use browser TTS
    // - provider 'runtype': only if browserFallback is enabled
    const useBrowserTts =
      !ttsConfig.provider ||
      ttsConfig.provider === 'browser' ||
      (ttsConfig.provider === 'runtype' && ttsConfig.browserFallback);
    if (!useBrowserTts) return;

    // Find the last assistant message with actual content
    const lastAssistant = [...this.messages]
      .reverse()
      .find(m => m.role === 'assistant' && m.content && !m.voiceProcessing);

    if (!lastAssistant || (only && !only.includes(lastAssistant.id))) return;

    // Skip if already spoken by Runtype provider's audio playback
    if (this.ttsSpokenMessageIds.has(lastAssistant.id)) {
      this.ttsSpokenMessageIds.delete(lastAssistant.id);
      return;
    }

    const text = resolveSpeakableText(lastAssistant.content);
    if (!text.trim()) return;

    // Route auto-speak through the same controller as the "Read aloud" button
    // so there's a single owner of the speech engine and the button reflects
    // playback state (and a single message can't be spoken by two paths at once).
    void this.readAloud.play(lastAssistant.id, {
      text,
      voice: ttsConfig.voice,
      rate: ttsConfig.rate,
      pitch: ttsConfig.pitch,
    });
  }

  /**
   * Pick the best available English voice from a list of SpeechSynthesisVoices.
   * Prefers high-quality remote/natural voices, then enhanced local voices,
   * then standard local voices. Retained for backwards compatibility; delegates
   * to the browser speech engine's picker.
   */
  static pickBestVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice {
    return pickBestVoice(voices);
  }

  /**
   * Toggle the per-message "Read aloud" action: play → pause → resume (or
   * play → stop when the engine can't pause). Speaks the assistant message's
   * text via the configured speech engine (browser Web Speech API by default,
   * or a hosted engine from `textToSpeech.createEngine`).
   */
  public toggleReadAloud(messageId: string): void {
    const message = this.messages.find(m => m.id === messageId);
    if (!message || message.role !== 'assistant') return;
    const text = resolveSpeakableText(message.content || '');
    if (!text.trim()) return;
    const tts = this.config.textToSpeech;
    this.readAloud.toggle(messageId, {
      text,
      voice: tts?.voice,
      rate: tts?.rate,
      pitch: tts?.pitch,
    });
  }

  /** Current read-aloud playback state for a message (`idle` unless active). */
  public getReadAloudState(messageId: string): ReadAloudState {
    return this.readAloud.stateFor(messageId);
  }

  /** Subscribe to read-aloud state changes. Returns an unsubscribe function. */
  public onReadAloudChange(listener: ReadAloudListener): () => void {
    return this.readAloud.onChange(listener);
  }

  /**
   * Stop any in-progress text-to-speech / read-aloud playback.
   */
  public stopSpeaking() {
    this.readAloud.stop();
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  /** Preserve streamed output and give its failure a distinct transcript row. */
  #appendDispatchError(error: unknown): void {
    for (const message of this.messages) message.streaming = false;
    const content = buildDispatchErrorContent(error, this.config.errorMessage);
    if (content) {
      this.#appendMessage({
        id: generateAssistantMessageId(),
        role: "assistant",
        content,
        dispatchError: true,
        localOnly: true,
        createdAt: new Date().toISOString(),
        sequence: this.#nextSequence(),
      });
    } else {
      this.callbacks.onMessagesChanged([...this.messages]);
    }
  }

  /**
   * Settle the approval-paused bubbles one stream decided but did not
   * complete. `undefined` settles those no live `connectStream` owns.
   */
  #flushApprovalSettles(streamToken: object | undefined) {
    for (const [id, token] of [...this.#approvalSettlesAtStreamEnd]) {
      if (token !== streamToken) continue;
      this.#approvalSettlesAtStreamEnd.delete(id);
      this.#settleApprovalPausedToolCall(id);
    }
  }

  /** Hand a dropped stream's pending settles to the end of all streaming. */
  #releaseApprovalSettles(streamToken: object) {
    for (const [id, token] of this.#approvalSettlesAtStreamEnd) {
      if (token === streamToken) this.#approvalSettlesAtStreamEnd.set(id, undefined);
    }
  }

  /**
   * Settle the tool bubble an approval paused when no terminal tool frame
   * arrived for it. Current servers run the paused call itself under its
   * original id; older ones re-issue it under a new id. Denied and timed-out
   * calls settle as failed; an approved call settles as superseded. A call
   * already complete is left alone.
   */

  #settleApprovalPausedToolCall(approvalMessageId: string) {
    const approval = this.messages.find((m) => m.id === approvalMessageId)?.approval;
    const id = approval?.toolCallId;
    const toolMessage = id && this.messages.find((m) => m.toolCall?.id === id);
    const tool = toolMessage && toolMessage.toolCall;
    if (!tool || tool.status === "complete" || approval.status === "pending") return;
    const settled: AgentWidgetToolCall = {
      ...tool,
      status: "complete",
      approvalStatus: approval.status,
      completedAt: Date.now(),
      ...(approval.status === "approved"
        ? { superseded: true }
        : { success: false, error: approval.status === "denied" ? "Denied" : "Approval timed out" }),
    };
    this.upsertMessage({ ...toolMessage, streaming: false, toolCall: settled });
  }

  #appendMessage(message: AgentWidgetMessage) {
    const withSequence = this.#ensureSequence(message);
    const last = this.messages[this.messages.length - 1];
    // Streamed messages almost always arrive in order. Re-sorting the whole
    // transcript on every append makes a long tool run O(N²) in date parses.
    this.messages =
      !last || this.#compareMessages(last, withSequence) <= 0
        ? [...this.messages, withSequence]
        : this.#sortMessages([...this.messages, withSequence]);
    this.callbacks.onMessagesChanged([...this.messages]);
  }

  private upsertMessage(message: AgentWidgetMessage) {
    const withSequence = this.#ensureSequence(message);
    const index = this.messages.findIndex((m) => m.id === withSequence.id);
    if (index === -1) {
      this.#appendMessage(withSequence);
      return;
    }

    this.messages = this.messages.map((existing, idx) => {
      if (idx !== index) return existing;
      // An unnamed skeleton precedes tool_start/await on a fresh parser. Keep
      // history until the populated event identifies the continuation. Also
      // retain actual terminal browser outcomes across replayed starts/deltas:
      // those events must never turn a completed mutation back into an await.
      if (
        existing.toolCall?.name && isWebMcpToolName(existing.toolCall.name) &&
        existing.toolCall.status === "complete" &&
        withSequence.toolCall?.id === existing.toolCall.id &&
        withSequence.toolCall.status !== "complete" &&
        ((withSequence.toolCall.status === "pending" && !withSequence.toolCall.name) ||
          (!existing.toolCall.interrupted &&
            (existing.toolCall.result !== undefined || existing.toolCall.success !== undefined) &&
            (!withSequence.agentMetadata?.executionId ||
              withSequence.agentMetadata.executionId === existing.agentMetadata?.executionId)))
      ) return existing;
      const merged = { ...existing, ...withSequence };
      // Preserve `ask_user_question` answered state across re-emissions.
      // Top-level merge would otherwise replace `agentMetadata` wholesale:      // post-resume events (e.g. `tool_complete` re-emitted from a stale
      // client-side cache) would wipe `askUserQuestionAnswered` and
      // `askUserQuestionAnswers`, causing the answered review card to
      // lose its answers and revert to "(skipped)" placeholders.
      if (
        existing.agentMetadata?.askUserQuestionAnswered === true &&
        withSequence.agentMetadata
      ) {
        merged.agentMetadata = {
          ...withSequence.agentMetadata,
          askUserQuestionAnswered: true,
          ...(existing.agentMetadata.askUserQuestionAnswers
            ? {
                askUserQuestionAnswers:
                  existing.agentMetadata.askUserQuestionAnswers,
              }
            : {}),
          // Keep awaiting flag false once resolved: never let a stale
          // re-emit flip us back to awaiting.
          awaitingLocalTool: false,
        };
      }
      // suggest_replies equivalent: preserve the persisted fire-and-forget
      // resolution across re-emissions. It is the only dedupe signal that
      // survives a hydration (the in-memory key sets are cleared), so a
      // stale await re-emit must not wipe it before
      // `isSuggestRepliesAlreadyResolved` checks it in the resolve path.
      if (
        existing.agentMetadata?.suggestRepliesResolved === true &&
        withSequence.agentMetadata
      ) {
        merged.agentMetadata = {
          ...(merged.agentMetadata ?? withSequence.agentMetadata),
          suggestRepliesResolved: true,
          awaitingLocalTool: false,
        };
      }
      // The accepted client-tool answer is what later proxy/agent turns
      // replay; a post-resume `tool_complete` re-emit carries a fresh
      // metadata skeleton and must not drop it.
      const clientToolAnswer = existing.agentMetadata?.clientToolAnswer;
      if (
        clientToolAnswer &&
        merged.agentMetadata &&
        !merged.agentMetadata.clientToolAnswer
      ) {
        merged.agentMetadata = { ...merged.agentMetadata, clientToolAnswer };
      }
      // Approval equivalent: `approval_complete` carries only the
      // resolution (approvalId, decision, resolvedBy): the runtime does not
      // re-send toolName/description/toolType/reason/parameters, so client.ts
      // rebuilds the approval with empty required fields and the optional
      // ones absent. A wholesale `approval` replacement would wipe that
      // context from the resolved bubble on the next full re-render (morph,
      // virtual-scroll re-mount, storage restore). Merge field-wise instead:
      // take the resolution from the incoming event, keep existing context
      // wherever the event is silent or empty.
      if (
        existing.approval &&
        withSequence.approval &&
        existing.approval.id === withSequence.approval.id
      ) {
        const prior = existing.approval;
        const incoming = withSequence.approval;
        merged.approval = {
          ...prior,
          ...incoming,
          executionId: incoming.executionId || prior.executionId,
          toolCallId: incoming.toolCallId || prior.toolCallId,
          toolName: incoming.toolName || prior.toolName,
          description: incoming.description || prior.description,
          toolType: incoming.toolType ?? prior.toolType,
          reason: incoming.reason ?? prior.reason,
          parameters: incoming.parameters ?? prior.parameters,
        };
      }
      // Auto-resolved local-tool equivalent (`webmcp:*` and the built-in
      // `suggest_replies`): once such a tool has started resolving (inflight)
      // or resolved, a duplicate `await` re-emit must not flip
      // `awaitingLocalTool` back to true and resurrect the "waiting on
      // local tool" UI. It also must not overwrite an existing running or
      // completed toolCall with the fresh running skeleton emitted by client.ts
      // for every await. resolveWebMcpToolCall's dedupe path returns
      // without re-touching the message, so correct the merge here (also avoids
      // a one-frame flash before that microtask runs).
      const reTcName = withSequence.toolCall?.name;
      const reExecId = withSequence.agentMetadata?.executionId;
      const reTcId = withSequence.toolCall?.id;
      if (
        reTcName &&
        isAutoResolvedLocalToolName(reTcName) &&
        reExecId &&
        reTcId &&
        withSequence.agentMetadata?.awaitingLocalTool === true
      ) {
        const reKey = `${reExecId}:${reTcId}`;
        const isInflight = this.webMcpInflightKeys.has(reKey);
        const isResolved = this.webMcpResolvedKeys.has(reKey);
        const existingToolName = existing.toolCall?.name;
        const hasCompletedTool =
          existing.agentMetadata?.executionId === reExecId &&
          existing.toolCall?.id === reTcId &&
          existingToolName !== undefined &&
          isAutoResolvedLocalToolName(existingToolName) &&
          existing.toolCall?.status === "complete" &&
          !existing.toolCall.interrupted;
        if (isInflight || isResolved || hasCompletedTool) {
          merged.agentMetadata = {
            ...(merged.agentMetadata ?? {}),
            awaitingLocalTool: false,
          };
          // Preserve the in-flight/completed tool state. For in-flight calls,
          // this keeps the original `startedAt`; for completed calls, it keeps
          // the measured duration/result even when the call completed with a
          // local/browser error and therefore was not promoted to resolved.
          merged.toolCall = existing.toolCall;
          merged.streaming = existing.streaming;
        }
      }
      return merged;
    });
    // The transcript is kept sorted, so only the merged entry can be out of
    // place; skip the full re-sort when it still sits between its neighbors.
    const updated = this.messages[index];
    const before = this.messages[index - 1];
    const after = this.messages[index + 1];
    if (
      (before && this.#compareMessages(before, updated) > 0) ||
      (after && this.#compareMessages(updated, after) > 0)
    ) {
      this.messages = this.#sortMessages(this.messages);
    }
    this.callbacks.onMessagesChanged([...this.messages]);
  }

  #ensureSequence(message: AgentWidgetMessage): AgentWidgetMessage {
    if (message.sequence !== undefined) {
      return { ...message };
    }
    return {
      ...message,
      sequence: this.#nextSequence()
    };
  }

  #nextSequence() {
    return this.#sequenceCounter++;
  }

  #compareMessages(a: AgentWidgetMessage, b: AgentWidgetMessage) {
    // Sort by createdAt timestamp first (chronological order)
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();
    if (!Number.isNaN(timeA) && !Number.isNaN(timeB) && timeA !== timeB) {
      return timeA - timeB;
    }

    // Fall back to sequence if timestamps are equal or invalid
    const seqA = a.sequence ?? 0;
    const seqB = b.sequence ?? 0;
    if (seqA !== seqB) return seqA - seqB;

    // Final fallback to ID
    return a.id.localeCompare(b.id);
  }

  #sortMessages(messages: AgentWidgetMessage[]) {
    return [...messages].sort((a, b) => this.#compareMessages(a, b));
  }
}
